import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import {
  Modal,
  Form,
  Select,
  Typography,
  Space,
  Divider,
  Alert,
  Input,
  InputNumber,
  DatePicker,
  Tooltip,
  Button,
  Grid,
  Row,
  Col,
  Collapse,
  Card,
} from "antd";
// Bridge antd estático: `Modal.confirm` y `message.x` no funcionan en
// antd v5 + React 19 — usar siempre `getModal()` / `getMsg()` para que
// el modal de confirmación al eliminar un evento del calendario y los
// toasts de error sean visibles. Round 2026-05-01.
import { getModal, getMsg } from "../../utils/antdStatic";
import { getIvaRate, getIvaPorcentaje, netoFromConIva } from "../../utils/iva";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
import { apiFetch } from "../../services/apiClient";
import {
  InfoCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { type Cleaner } from "./useCalendarioLogic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import {
  type Third,
  type MoneyLine,
  type MoneyLines,
  daysBetween,
  n,
  roundPeso,
  cmpISODate,
  thirdIdx,
  thirdToTimeStart,
  thirdToTimeEnd,
  hhmmToMin,
  minToThird,
  dayjsOrDefaultTime,
  toIsoWithSeconds,
  normalizeMoneyLines,
  sumMoneyLines,
} from "./calendarCreateModal.helpers";
import {
  StartThirdField,
  EndThirdField,
  StartTimeField,
  EndTimeField,
} from "./components/ThirdAndTimeFields";
import BlockMaintenanceFields from "./components/BlockMaintenanceFields";
import CleaningFields from "./components/CleaningFields";
import { ahoraChile } from "../../utils/fechaChile";
import { consultaMismoDia } from "../../utils/aseosMismoDia";
import { confirmarAseosMismoDia } from "../aseos/components/AvisoAseosMismoDia";

// Lazy load — round F2 2026-04-29. ReparticionDineroDiagram usa
// reactflow (~84 KB) para el diagrama Sankey. Solo se renderiza
// cuando el admin abre el modal de crear/editar reserva, así que
// no debería estar en el main bundle.
const ReparticionDineroDiagram = React.lazy(
  () => import("./ReparticionDineroDiagram"),
);

// Reexport para mantener la API publica (otros archivos importan estos
// tipos desde este modulo).
export type { Third, MoneyLine, MoneyLines };

const { Text } = Typography;

export type CreateKind = "cleaning" | "booking" | "block" | "maintenance";

export type RangeSelection = {
  propertyId: number;
  propertyName: string | null;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD

  // presets opcionales (p.ej. cuando se crea con "Añadir evento")
  startThird?: Third;
  endThird?: Third;
  tasaComisionAf?: number;
  costoAseo?: number;
  sueldoAseo?: number;

  // Subitems de limpieza de la propiedad (netos/totales). Necesarios para
  // calcular Ingreso AF en el modal de booking. Ver
  // docs/modulo-arriendos-precios.md §Desglose de limpieza.
  transporteNeto?: number;
  transporteTotal?: number;
  materialesNeto?: number;
  materialesTotal?: number;
  lavanderiaNeto?: number;
  lavanderiaTotal?: number;
  hasLavanderiaAf?: boolean;

  /**
   * Hints opcionales para el MODO "Día completo": fuerzan kind inicial,
   * horas y (cuando aplica) saltan los defaults del useEffect de
   * create. El admin puede cambiarlos después — no son locks.
   */
  presetKind?: CreateKind;
  presetStartTime?: string; // "HH:mm"
  presetEndTime?: string;   // "HH:mm"
};

/**
 * Descuento con tipo. Ver docs/modulo-arriendos-precios.md §Tipos de descuento.
 *   - "ajuste_total": el huésped pagó menos → reduce precioNochesEfectivo
 *     y se recalculan comisiones AF en cascada. Cliente y AF pierden proporcional.
 *   - "solo_af": pérdida exclusiva de AF. El Cliente conserva su ingreso original.
 */
export type DiscountTipo = "ajuste_total" | "solo_af";
export type DiscountLine = { name: string; _f_val: number; tipo: DiscountTipo };
export type DiscountLines = DiscountLine[];

export type CreatePayload = {
  kind: CreateKind;
  id?: number | string;
  propertyId: number;

  startDate: string; // YYYY-MM-DD
  startThird: Third;
  endDate: string; // YYYY-MM-DD
  endThird: Third;
  startTime?: string; // HH:mm (solo para limpieza)
  endTime?: string; // HH:mm (solo para limpieza)
  // sugerencias de timestamp (para backend o logs)
  startISO: string; // e.g. 2026-02-02T11:00:00
  endISO: string; // e.g. 2026-02-04T23:59:59

  // ===== extras opcionales según kind =====
  // cleaning
  modoAsignacion?: "designado" | "abierto";
  idAseador?: number;
  nombreAseador?: string;
  costoLimpieza?: number;
  sueldoAseador?: number;
  horaInicio?: string; // HH:mm
  urgencia?: string;
  estado?: string;
  // Round 2026-05-13 — `rolAF` y `pagado_a` eliminados del modelo Cleaning.
  // El único campo de flujo financiero es `pagado_por.tipo` (Quién paga).
  pagado_por?: { tipo: number; nombre?: string };
  observaciones?: string;
  /**
   * Solo lectura en edit-mode: FK al arriendo (`reserva_id`) cuando el aseo es
   * VINCULADO/AUTO. NO se envía en el payload (buildPayload lo ignora); sirve
   * para que el modal sepa si debe deshabilitar "Propietario/Cliente paga"
   * (regla anti-doble-cobro §6.4). null/undefined = aseo aledaño.
   */
  reservaId?: number | null;

  // block/maintenance
  razon?: string;
  notas?: string;

  // booking
  ingreso?: string | Dayjs; // YYYY-MM-DD or Dayjs (form uses Dayjs objects)
  salida?: string | Dayjs; // YYYY-MM-DD or Dayjs (form uses Dayjs objects)
  nombreHuesped?: string;
  telefonoHuesped?: string;
  cantidadHuespedes?: number;
  fechaCreacion?: string; // ISO o datetime-local string

  noches?: number;

  moneda?: "CLP";
  // "Directa" en creación manual. En EDICIÓN llega el origen real del
  // arriendo ("AirBnb" / "Booking" / "Despegar" / "Stays") — el autocalc
  // lo usa para saber si `precioNochesClp` persistido viene BRUTO (manual)
  // o ya NETO de comisión de plataforma (API). Ver fix 2026-08-13.
  nombrePlataforma?: string;
  idPlataforma?: string;

  precioNocheClp?: number; // input (obligatorio)
  precioNochesClp?: number; // autocalculado (precioNocheClp * noches)

  limpiezaClp?: number; // input (obligatorio)
  comisionPlataformaClp?: number; // opcional

  // Override per-arriendo del desglose de limpieza (§23 docs). Si es
  // null/undefined o preset="default", el backend persiste null y la
  // reserva usa los subitems de la propiedad. Si hay preset explícito,
  // persistimos el JSONB completo con los totales de cada subitem.
  limpiezaSubitemsOverride?: {
    preset: "default" | "sin_lavanderia" | "costo_0" | "custom";
    aseador: number;
    transporte_neto: number;
    transporte_iva: number;
    transporte_total: number;
    materiales_neto: number;
    materiales_iva: number;
    materiales_total: number;
    lavanderia_neto: number;
    lavanderia_iva: number;
    lavanderia_total: number;
  } | null;

  // §28 — Cargos adicionales al arriendo. null = sin cargos (valor legacy).
  adicionalesArriendo?: {
    extensiones_noches?: Array<{
      noches: number;
      precio_por_noche_clp: number;
    }>;
    salida_tardia?: {
      monto_clp: number;
      hora_nueva?: string; // "HH:mm"
    } | null;
    otros?: Array<{
      nombre: string;
      monto_clp: number;
      rol_af: "paga" | "recibe" | "intermediario";
      pagado_por?: { tipo: number; nombre?: string };
      pagado_a?: { tipo: number; nombre?: string };
    }>;
  } | null;

  extrasSinComisionClp?: MoneyLines; // JSONB [{name,_f_val}]
  extrasConComisionClp?: MoneyLines; // JSONB [{name,_f_val}]
  totalExtrasClp?: number; // autocalculado (sin + con)

  descuentoClp?: DiscountLines | any; // lista de descuentos con tipo

  tasaComisionAf?: number; // se lee de la propiedad (range.tasaComisionAf)
  comisionAfNochesClp?: number; // autocalculado
  comisionAfExtrasClp?: number; // autocalculado
  ivaComisionDeNochesClp?: number; // autocalculado 19%
  ivaComisionDeExtrasClp?: number; // autocalculado 19%

  reservaTotalClp?: number; // autocalculado
  /**
   * En UI y docs lo llamamos "Ingreso Cliente". La columna DB se mantiene
   * como `reserva_total_propietario` por compatibilidad — ver
   * docs/modulo-arriendos-precios.md §Terminología.
   */
  reservaTotalPropietario?: number; // autocalculado (= Ingreso Cliente)
  ingresoAfClp?: number; // autocalculado (lo que gana AF empresa)

  estadoPago?: string; // opcional
  tipoPago?: string; // opcional
  action?: "creacion" | "edicion" | "eliminacion";
  tipo?: "reservado" | "pre-reserva";
  notaInterna?: string;

  inmutable?: boolean; // false
};

type Props = {
  open: boolean;
  range: RangeSelection | null;
  onClose: () => void;
  // onCreate puede ser sync o async. Lo tratamos como Promise<void>|void:
  // el modal hace `await onCreate(payload)` para saber cuándo terminó el
  // POST y recién entonces desbloquea el botón (ver confirmLoading abajo).
  onCreate: (payload: CreatePayload) => void | Promise<void>;
  updateEvent: (payload: CreatePayload) => Promise<void>;
  deleteEvent: (kind: string, id: number | string) => Promise<boolean>;
  aseadores: Cleaner[];
  loadingAseadoras: boolean;
  mode?: "create" | "edit";
  initialValues?: Partial<CreatePayload> | null;
  allowedKinds?: CreateKind[];
  /**
   * Inyectado desde calendario.tsx. Devuelve true si la propiedad+fecha
   * tiene los 3 tercios ocupados — en ese caso el DatePicker bloquea ese
   * día para inicio/fin (no hay lugar donde poner el evento).
   * En modo edición se pasa `exclude` (id+kind del evento editado) para que
   * los días cubiertos por el PROPIO evento no queden deshabilitados —
   * si no, era imposible achicar un bloqueo dentro de su período.
   */
  isDayFullyOccupied?: (
    propertyId: number,
    iso: string,
    exclude?: { id?: number | string | null; kind?: string | null },
  ) => boolean;
  /**
   * Check de colisión a nivel de tercio para admin/superadmin. Devuelve
   * mensaje humano con el conflicto o null si está libre. Se ejecuta
   * antes del POST/PATCH; en edit-mode excluimos el propio evento para
   * no chocar consigo mismo.
   */
  checkOverlap?: (opts: {
    propertyId: number;
    startDate: string;
    endDate: string;
    startThird: Third;
    endThird: Third;
    excludeId?: number | string | null;
    excludeKind?: string | null;
  }) => string | null;
};

export default function CalendarCreateModal({
  open,
  range,
  onClose,
  onCreate,
  aseadores,
  loadingAseadoras,
  mode,
  initialValues,
  updateEvent,
  deleteEvent,
  allowedKinds,
  isDayFullyOccupied,
  checkOverlap,
}: Props) {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const initialKind: CreateKind = "cleaning";

  // Guard anti doble-submit. Sin esto, un admin clickeando rápido el botón
  // "Crear" creaba múltiples copias del mismo evento (bloqueo, aseo, etc.)
  // porque la llamada HTTP al backend no era sincrónica y el botón quedaba
  // activo durante el fetch. `submitting=true` ⇒ Modal.confirmLoading muestra
  // spinner y desactiva el botón; el guard de entrada en `onOk` es redundante
  // pero defensivo por si el click se dispara antes de que React re-renderice.
  const [submitting, setSubmitting] = useState(false);

  const todayISO = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const esPasado = useMemo(() => {
    if (!range) return false;
    return cmpISODate(range.endDate, todayISO) < 0;
  }, [range, todayISO]);

  const esFuturo = useMemo(() => {
    if (!range) return false;
    return cmpISODate(range.startDate, todayISO) > 0;
  }, [range, todayISO]);

  useEffect(() => {
    if (!open || !range) return;

    if (mode === "edit" && !initialValues) return;
    if (mode === "edit" && initialValues) {
      const startThird: Third =
        (initialValues.startThird as Third) ?? range.startThird ?? "morning";
      const endThird: Third =
        (initialValues.endThird as Third) ?? range.endThird ?? "evening";

      const fallbackStart = thirdToTimeStart(startThird).slice(0, 5);
      const fallbackEnd = thirdToTimeEnd(endThird).slice(0, 5);

      const toDayjsTime = (v: any, fallbackHHMM: string) => {
        if (dayjs.isDayjs(v)) return v;
        if (typeof v === "string" && v.trim())
          return dayjsOrDefaultTime(v.slice(0, 5));
        return dayjsOrDefaultTime(fallbackHHMM);
      };

      // IMPORTANTE: `...initialValues` va ANTES que las conversiones a
      // Dayjs. Los TimePickers/DatePickers de AntD llaman `.isValid()`
      // sobre su value; si reciben un string ("15:00", "2026-04-21T...")
      // en vez de un Dayjs, AntD crashea con "e.isValid is not a
      // function" y la página se queda en blanco. Por eso spreadeamos
      // primero el payload crudo (kind, razon, notas, huesped, etc.) y
      // luego sobrescribimos SÓLO los campos que sí tienen que ser Dayjs.
      form.setFieldsValue({
        ...initialValues,

        // kind: respeta el del evento (si viene).
        kind: initialValues.kind ?? (initialValues as any).kind,
        startThird,
        endThird,

        // TimePickers requieren Dayjs; `initialValues.startTime` puede ser
        // string "HH:mm" o undefined — toDayjsTime normaliza ambos casos.
        startTime: toDayjsTime((initialValues as any).startTime, fallbackStart),
        endTime: toDayjsTime((initialValues as any).endTime, fallbackEnd),

        // Booking: `ingreso`/`salida` deberían venir ya como Dayjs desde
        // el caller (calendario.tsx onEditEvent los wrappea). Como
        // defensa por si algún día llegaran como string, los pasamos por
        // el mismo helper — si ya son Dayjs los devuelve tal cual.
        ...((initialValues as any).ingreso != null
          ? {
              ingreso: dayjs.isDayjs((initialValues as any).ingreso)
                ? (initialValues as any).ingreso
                : dayjs((initialValues as any).ingreso),
            }
          : {}),
        ...((initialValues as any).salida != null
          ? {
              salida: dayjs.isDayjs((initialValues as any).salida)
                ? (initialValues as any).salida
                : dayjs((initialValues as any).salida),
            }
          : {}),
        // fechaCreacion también es un DatePicker — si viene del backend
        // como ISO string, convertimos.
        ...((initialValues as any).fechaCreacion != null
          ? {
              fechaCreacion: dayjs.isDayjs(
                (initialValues as any).fechaCreacion,
              )
                ? (initialValues as any).fechaCreacion
                : dayjs((initialValues as any).fechaCreacion),
            }
          : {}),

        // Fix 2026-08-13 — arriendo SIN override de limpieza (preset
        // "default"): pintar los 4 inputs read-only del desglose con los
        // subitems de la propiedad. Antes lo hacía un efecto reactivo que
        // también corría con override presente y lo pisaba (ver
        // handleLimpiezaPresetChange); ahora la edición solo repinta desde
        // el onChange del Select, así que el estado inicial se siembra acá.
        ...(initialValues.kind === "booking" &&
        (initialValues as any).limpiezaCustomAseador === undefined
          ? {
              limpiezaCustomAseador: n(range.sueldoAseo),
              limpiezaCustomTransporteTotal: n(range.transporteTotal),
              limpiezaCustomMaterialesTotal: n(range.materialesTotal),
              limpiezaCustomLavanderiaTotal: n(range.lavanderiaTotal),
            }
          : {}),

        action: "edicion",
      });

      return;
    }
  }, [open, range, form, esPasado, mode, initialValues]);

  // calendarCreateModal.tsx

  useEffect(() => {
    if (!open || !range) return;
    if (mode === "edit") return; // 👈 este efecto es SOLO para create

    // Opcional pero recomendable: reinicia al abrir para que no queden residuos del form anterior
    form.resetFields();

    // kind por defecto:
    //  - si range.presetKind vino (p.ej. desde "Día completo") se respeta,
    //  - si allowedKinds fija la lista, tomamos el primero,
    //  - si no, mismo día → aseo, multi-día → arriendo.
    const isSameDay = range.startDate === range.endDate;
    const defaultKind: CreateKind =
      range.presetKind ??
      (allowedKinds?.[0] as CreateKind) ??
      (isSameDay ? "cleaning" : "booking");

    // Defaults por tipo (si no vienen preset):
    //  - booking:      evening (15:00) → morning (10:59 día siguiente)
    //  - cleaning:     mid (11:00) → mid (14:59) mismo día
    //  - block / maintenance:
    //        evening (15:00) → morning (10:59 día siguiente), 1 noche.
    //        Admin PUEDE cambiar tercios/horas; es sólo el default. Los
    //        rangos horarios siguen atados al tercio (ver disabledTime en
    //        el TimePicker).
    const startThird: Third =
      defaultKind === "booking"
        ? "evening"
        : defaultKind === "cleaning"
          ? (range.startThird ?? "mid")
          : (range.startThird ?? "evening"); // block / maintenance
    const endThird: Third =
      defaultKind === "booking"
        ? "morning"
        : defaultKind === "cleaning"
          ? (range.endThird ?? "mid")
          : (range.endThird ?? "morning"); // block / maintenance

    const fallbackStart = thirdToTimeStart(startThird).slice(0, 5);
    const fallbackEnd = thirdToTimeEnd(endThird).slice(0, 5);

    const costoAseo = Number(range.costoAseo ?? 0);
    const sueldoAseo = Number(range.sueldoAseo ?? 0);

    // Para block/maintenance con tercios evening → morning por defecto,
    // el evento cruza medianoche: end debe ser startDate + 1 día.
    const blockDefaultSpansNight =
      (defaultKind === "block" || defaultKind === "maintenance") &&
      startThird === "evening" &&
      endThird === "morning" &&
      range.startDate === range.endDate;

    // Heredar fechas preseleccionadas desde la selección del calendario.
    // Si el usuario marcó una ÚNICA fecha (startDate === endDate) y no
    // hay preset (ej. "Día completo"), interpretamos que el fin aún no
    // se conoce: para arriendo dejamos `salida` vacía (el admin la
    // completa manualmente). Para block/maintenance mantenemos el
    // default 1-noche vía `blockDefaultSpansNight`. Para cleaning el
    // evento es intra-día y no expone DatePicker de salida.
    const isSingleDateSelection =
      range.startDate === range.endDate && !range.presetKind;
    const leaveSalidaEmpty =
      isSingleDateSelection && defaultKind === "booking";

    const defaults: Record<string, any> = {
      kind: defaultKind,

      startThird,
      endThird,

      startTime: dayjsOrDefaultTime(fallbackStart),
      endTime: dayjsOrDefaultTime(fallbackEnd),

      ingreso: dayjs(range.startDate),
      // Si es booking con selección de una sola fecha, salida queda vacía.
      salida: leaveSalidaEmpty ? undefined : dayjs(range.endDate),

      fechaCreacion: ahoraChile(),

      costoLimpieza: costoAseo || undefined,
      limpiezaClp: costoAseo || undefined,
      sueldoAseador: sueldoAseo || undefined,

      // Desglose de limpieza: arranca en "default" (usa la propiedad).
      // Los 4 inputs custom se prellenan con los valores de la propiedad
      // vía el useEffect de sincronización, así el admin puede ver qué
      // cobra la propiedad aunque todavía no haya tocado el desglose.
      limpiezaPreset: "default",

      action: "creacion",
    };

    if (defaultKind === "cleaning") {
      // Tercio mid: 11:00 → 14:59 (último minuto ocupado del tercio,
      // ver docs/calendario-logica.md §1.2). Nunca persistir "15:00" como
      // hora_termino — 15:00 pertenece a evening.
      defaults.startTime = dayjs("11:00", "HH:mm");
      defaults.endTime = dayjs("14:59", "HH:mm");
      defaults.pagado_por = { tipo: 0 };
    }

    if (defaultKind === "block" || defaultKind === "maintenance") {
      // Horas por defecto obligatorias del dominio: 15:00 → 10:59 (1 noche).
      defaults.startTime = dayjs("15:00", "HH:mm");
      defaults.endTime = dayjs("10:59", "HH:mm");
      // Si el range fue un único día, extendemos la salida al día siguiente
      // para reflejar "cruza la noche". Admin puede modificarlo.
      if (blockDefaultSpansNight) {
        defaults.salida = dayjs(range.startDate).add(1, "day");
      }
    }

    // Overrides por preset (ej. "Día completo"):
    //   - presetStartTime / presetEndTime: horas exactas desde 00:00 a
    //     23:59, ignorando los defaults por tercio. Los tercios vienen
    //     en range.startThird / range.endThird y ya se aplicaron arriba
    //     (morning → evening en este caso).
    //   - La SALIDA en "Día completo" es el mismo día de la entrada
    //     (no cruza la noche), así que sobre-escribimos cualquier +1
    //     día que haya quedado del bloque de arriba.
    if (range.presetStartTime) {
      defaults.startTime = dayjs(range.presetStartTime, "HH:mm");
    }
    if (range.presetEndTime) {
      defaults.endTime = dayjs(range.presetEndTime, "HH:mm");
    }
    if (range.presetKind) {
      // Día completo es INTRA-DÍA: salida colapsa a ingreso.
      defaults.salida = dayjs(range.startDate);
    }

    form.setFieldsValue(defaults);
  }, [open, range, mode, form, allowedKinds]);

  // FIX 2026-07-11 — el fallback en modo EDICIÓN debe respetar el `kind` del
  // evento editado, no forzar "cleaning". `Form.useWatch("kind")` devuelve
  // undefined en el PRIMER render (el form se hidrata en un useEffect posterior,
  // ~L370), así que caía a `initialKind="cleaning"`. Al editar un ARRIENDO eso
  // montaba transitoriamente <CleaningFields> con aseoId = id de la RESERVA, y
  // <AseoNotesSection> disparaba GET /aseos/{idReserva}/notas → 404 "Aseo no
  // encontrado" (id de reserva ≠ id de aseo). Derivando el fallback de
  // `initialValues.kind` (que calendario.tsx siembra como "booking"/"cleaning"),
  // el primer render ya es del tipo correcto y no se monta CleaningFields.
  const kind: CreateKind =
    Form.useWatch("kind", form) ??
    (mode === "edit"
      ? ((initialValues?.kind as CreateKind) ?? initialKind)
      : initialKind);
  const modoAsignacion: "designado" | "abierto" =
    Form.useWatch("modoAsignacion", form) ?? "designado";

  // ============================================================
  // Aseo — watches + memos
  // ============================================================
  // Opciones del Select de aseador — memoizadas para que el mismo
  // array-reference persista entre renders. Cambiar la referencia en
  // cada render hacía que el dropdown se re-creara y, en ciertos casos
  // edge (búsqueda + conditional rendering del Col padre), la
  // selección se perdiera visualmente aunque idAseador SÍ estuviera
  // guardado en form state. Memoizando cortamos esa re-creación.
  const aseadorOptions = useMemo(
    () =>
      aseadores.map((a) => ({
        value: a.id,
        label: a.nombre,
      })),
    [aseadores],
  );

  // Preset del desglose de costo del aseo ("default" | "custom").
  // Al cambiar a default se re-pintan los 4 custom inputs con los
  // valores de la propiedad; en "custom" el admin los edita libremente.
  const aseoCostPreset: "default" | "custom" =
    Form.useWatch("aseoCostPreset", form) ?? "default";
  const aseoCustomAseador = Form.useWatch("aseoCustomAseador", form);
  const aseoCustomTransporteTotal = Form.useWatch(
    "aseoCustomTransporteTotal",
    form,
  );
  const aseoCustomMaterialesTotal = Form.useWatch(
    "aseoCustomMaterialesTotal",
    form,
  );
  const aseoCustomLavanderiaTotal = Form.useWatch(
    "aseoCustomLavanderiaTotal",
    form,
  );

  const aseoCostoTotal =
    n(aseoCustomAseador) +
    n(aseoCustomTransporteTotal) +
    n(aseoCustomMaterialesTotal) +
    n(aseoCustomLavanderiaTotal);

  // Round 2026-05-13 — `rolAFWatch` eliminado. El aseo ya no lleva
  // `rol_de_aloja_facil`; el flujo se determina por `pagado_por.tipo`.

  // Propagamos la suma al campo legacy `costoLimpieza` y el pago aseador
  // al campo `sueldoAseador` — así el payload al backend sigue con el
  // mismo shape y no hay que migrar consumers downstream. Solo sincroniza
  // para kind=cleaning.
  useEffect(() => {
    if (kind !== "cleaning") return;
    form.setFieldsValue({
      costoLimpieza: aseoCostoTotal,
      sueldoAseador: n(aseoCustomAseador),
    });
  }, [kind, aseoCostoTotal, aseoCustomAseador, form]);

  // Cuando el preset cambia a "default", re-pinta los 4 inputs con los
  // subitems de la propiedad (valor canónico). En "custom" respetamos
  // lo que escribe el admin.
  //
  // IMPORTANTE: en edit-mode, la PRIMERA ejecución del efecto corre
  // inmediatamente después del mount, cuando el form ya se hidrató con
  // los valores stored del aseo. Repintar desde la propiedad en ese
  // momento sobrescribe la data del admin (bug reportado: "edición de
  // aseo no guarda, queda igual al guardar con preset=default"). La ref
  // `aseoDefaultInitializedRef` skipea la primera corrida en edit-mode:
  // solo se aplica el repintado cuando el admin cambia el preset
  // manualmente (runs posteriores).
  // Snapshot de los 4 subitems custom tomado al pasar a "Por defecto", para
  // RESTAURARLOS si el admin vuelve a "Personalizada". Sin esto, la ida y
  // vuelta por "Por defecto" dejaba los inputs con los valores de la
  // propiedad y guardar persistía un override custom con montos
  // predeterminados (bug reportado 2026-08-02).
  const aseoCustomSnapshotRef = useRef<Record<string, number> | null>(null);
  useEffect(() => {
    if (!open) {
      aseoCustomSnapshotRef.current = null;
    }
  }, [open]);

  // Cambio MANUAL del "Modo" del desglose (Select en CleaningFields). Vive en
  // el onChange — evento inequívocamente iniciado por el admin — y NO en un
  // efecto reactivo: el efecto anterior también corría en pasadas de
  // hidratación/mount con watches stale y necesitaba un ref-guard
  // (aseoDefaultInitializedRef, fix cc9000d) que además se tragaba el primer
  // toggle real al reabrir el modal (review round 2026-08-02). Con el handler,
  // la regla "en edición no se pisan los valores cargados" es estructural: la
  // hidratación siembra los 4 inputs y nada más los toca salvo este Select.
  const handleAseoPresetChange = (next: "default" | "custom") => {
    if (next === "custom") {
      // Volver a "Personalizada": restaurar lo tipeado antes del repintado.
      // Sin snapshot (primer cambio a custom) se conservan los valores
      // actuales — en creación, los defaults de la propiedad ya pintados.
      if (aseoCustomSnapshotRef.current) {
        form.setFieldsValue(aseoCustomSnapshotRef.current);
        aseoCustomSnapshotRef.current = null;
      }
      return;
    }
    // → "Por defecto": guardar lo actual (para poder restaurarlo) y repintar
    // con los valores canónicos de la propiedad.
    aseoCustomSnapshotRef.current = form.getFieldsValue([
      "aseoCustomAseador",
      "aseoCustomTransporteTotal",
      "aseoCustomMaterialesTotal",
      "aseoCustomLavanderiaTotal",
    ]);
    form.setFieldsValue({
      aseoCustomAseador: n(range?.sueldoAseo),
      aseoCustomTransporteTotal: n(range?.transporteTotal),
      aseoCustomMaterialesTotal: n(range?.materialesTotal),
      aseoCustomLavanderiaTotal: n(range?.lavanderiaTotal),
    });
  };

  // Pintado inicial de los 4 inputs custom SOLO en CREACIÓN (los defaults del
  // create-effect no incluyen aseoCustom*; así el admin ve qué cobra la
  // propiedad aunque no haya tocado el desglose). En EDICIÓN no corre nunca:
  // la hidratación siembra los valores stored y solo el Select los cambia.
  useEffect(() => {
    if (!open || mode === "edit") return;
    if (kind !== "cleaning") return;
    if (aseoCostPreset === "custom") return;
    form.setFieldsValue({
      aseoCustomAseador: n(range?.sueldoAseo),
      aseoCustomTransporteTotal: n(range?.transporteTotal),
      aseoCustomMaterialesTotal: n(range?.materialesTotal),
      aseoCustomLavanderiaTotal: n(range?.lavanderiaTotal),
    });
  }, [open, mode, kind, aseoCostPreset, range, form]);

  // Round 2026-05-13 — el aseo ya no lleva `rolAF` ni `pagado_a`. El
  // único campo de flujo es `pagado_por.tipo` que se elige directamente
  // en CleaningFields. El efecto previo que sincronizaba `pagado_por` /
  // `pagado_a` según `rolAF` quedó obsoleto.

  const canRender = !!open && !!range;
  const startThirdWatch: Third = Form.useWatch("startThird", form) ?? "morning";
  const endThirdWatch: Third = Form.useWatch("endThird", form) ?? "evening";
  const ingreso = Form.useWatch("ingreso", form);
  const salida = Form.useWatch("salida", form);

  const precioNocheClp = Form.useWatch("precioNocheClp", form);
  const precioNochesClp = Form.useWatch("precioNochesClp", form);
  const limpiezaClp = Form.useWatch("limpiezaClp", form);

  const comisionPlataformaClp = Form.useWatch("comisionPlataformaClp", form);
  const extrasSinComisionClp = Form.useWatch("extrasSinComisionClp", form);
  const extrasConComisionClp = Form.useWatch("extrasConComisionClp", form);
  const descuentoClp = Form.useWatch("descuentoClp", form);

  // ============================================================
  // §28 — Cargos adicionales al arriendo
  // ============================================================
  // Extensión de noches: N noches extra a precio especial. Entra al
  //   `precioNochesEfectivo` del autocalc — devenga comisión AF normal.
  // Salida tardía: monto + nueva hora_salida. Entra como un extra con
  //   comisión AF implícito.
  // Otros: lista de flujos ad-hoc con rolAF + pagado_por/a. NO entra a
  //   reservaTotal — se registra aparte en ledgers de las entidades.
  const extensionesNochesWatch = Form.useWatch("extensionesNoches", form);
  const salidaTardiaWatch = Form.useWatch("salidaTardia", form);
  const otrosCargosWatch = Form.useWatch("otrosCargos", form);
  // Total de noches calculado por el autocalc (ya incluye auto-extensiones).
  // Usado en el helper text del promedio ponderado.
  const nochesWatch = Form.useWatch("noches", form);

  const totalExtensionesNoches = useMemo(() => {
    if (!Array.isArray(extensionesNochesWatch)) return 0;
    return extensionesNochesWatch.reduce((acc, item) => {
      const noches = Number(item?.noches) || 0;
      const precio = Number(item?.precio_por_noche_clp) || 0;
      return acc + noches * precio;
    }, 0);
  }, [extensionesNochesWatch]);

  // Conteo de noches extra (no monto). Necesario para auto-extender la
  // `salida` de la reserva: cada noche añadida al array corre la fecha de
  // salida en +1 día en el calendario — refleja la realidad de que el
  // huésped se queda más tiempo. Ver §28 modulo-arriendos-precios.
  const totalExtensionesNochesCount = useMemo(() => {
    if (!Array.isArray(extensionesNochesWatch)) return 0;
    return extensionesNochesWatch.reduce(
      (acc, item) => acc + (Number(item?.noches) || 0),
      0,
    );
  }, [extensionesNochesWatch]);

  const totalSalidaTardia = useMemo(() => {
    return Number(salidaTardiaWatch?.monto_clp) || 0;
  }, [salidaTardiaWatch]);

  // Ref que guarda el total de noches-extensión YA reflejado en `salida`/
  // `noches`. Solo aplicamos deltas DESPUÉS de que el baseline quedó
  // sincronizado con el form hidratado.
  //   null   = modal cerrado / form aún no hidratado → NO aplicar deltas.
  //   number = baseline seteado, deltas futuros son reales.
  //
  // El bug anterior: setear el baseline en 0 al abrir y dejar que el form
  // se hidratara después producía un delta espurio (0 → savedCount) que
  // corría `salida` y `noches` hacia adelante cada vez que se abría un
  // booking con extensiones guardadas. Síntomas: al editar un arriendo
  // se veían noches crecidas, precio por noche recalculado (desaparece
  // el valor original). Fix: esperamos a que el form HYDRATE sus
  // extensiones — solo entonces sellamos el baseline.
  const prevExtNightsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      prevExtNightsRef.current = null;
      return;
    }
    // Ya inicializado — el segundo efecto se encarga de aplicar deltas.
    if (prevExtNightsRef.current !== null) return;

    const savedCount =
      mode === "edit" &&
      Array.isArray((initialValues as any)?.extensionesNoches)
        ? (initialValues as any).extensionesNoches.reduce(
            (acc: number, e: any) => acc + (Number(e?.noches) || 0),
            0,
          )
        : 0;

    // En create: savedCount=0 y el form arranca vacío → match inmediato.
    // En edit: esperamos a que el `Form.List` de extensionesNoches se
    // hidrate desde initialValues — el efecto se re-dispara por la
    // dependencia `totalExtensionesNochesCount` hasta que coincidan.
    if (totalExtensionesNochesCount === savedCount) {
      prevExtNightsRef.current = savedCount;
    }
  }, [open, mode, initialValues, totalExtensionesNochesCount]);

  useEffect(() => {
    // El efecto actúa solo para bookings. Aseos/bloqueos/mantenciones no
    // tienen `extensionesNoches`.
    const currentKind = form.getFieldValue("kind") ?? kind;
    if (currentKind !== "booking") return;
    if (!open) return;
    // Baseline aún no inicializado (esperando hidratación). NO aplicar
    // deltas — sino corremos salida con valores previos a la hidratación
    // del form (bug del "precio por noche que desaparece").
    if (prevExtNightsRef.current === null) return;

    const delta = totalExtensionesNochesCount - prevExtNightsRef.current;
    if (delta === 0) return;

    const currentSalida = form.getFieldValue("salida");
    const currentNoches = Number(form.getFieldValue("noches") || 0);

    if (dayjs.isDayjs(currentSalida) && currentSalida.isValid()) {
      form.setFieldsValue({
        salida: currentSalida.add(delta, "day"),
        noches: currentNoches + delta,
      });
    } else if (currentSalida) {
      const d = dayjs(currentSalida);
      if (d.isValid()) {
        form.setFieldsValue({
          salida: d.add(delta, "day"),
          noches: currentNoches + delta,
        });
      }
    }
    prevExtNightsRef.current = totalExtensionesNochesCount;
  }, [open, kind, form, totalExtensionesNochesCount]);

  // ============================================================
  // Desglose de limpieza — override per-arriendo
  // ============================================================
  // Por defecto los subitems (pago aseador, transporte, materiales,
  // lavandería) vienen de la Property. Acá permitimos que el admin los
  // override sólo para ESTE arriendo, eligiendo uno de 4 presets:
  //   - default        → usa los de la propiedad
  //   - sin_lavanderia → todos los subitems de la propiedad excepto
  //                       lavandería, que queda en 0
  //   - costo_0        → todos los subitems en 0 (la limpieza no se cobra)
  //   - custom         → admin define los totales de cada subitem a mano
  //
  // El resultado se persiste en `reservas.limpieza_subitems_override`
  // (JSONB) — null si es "default" (ver backend stays.controller.js).
  const limpiezaPreset: "default" | "sin_lavanderia" | "costo_0" | "custom" =
    Form.useWatch("limpiezaPreset", form) ?? "default";
  const limpiezaCustomAseador = Form.useWatch("limpiezaCustomAseador", form);
  const limpiezaCustomTransporteTotal = Form.useWatch(
    "limpiezaCustomTransporteTotal",
    form,
  );
  const limpiezaCustomMaterialesTotal = Form.useWatch(
    "limpiezaCustomMaterialesTotal",
    form,
  );
  const limpiezaCustomLavanderiaTotal = Form.useWatch(
    "limpiezaCustomLavanderiaTotal",
    form,
  );

  /**
   * Computa los subitems EFECTIVOS según el preset activo. Para "custom"
   * derivamos el neto y el IVA de cada subitem con IVA (transporte,
   * materiales, lavandería) con regla `neto = round(total / 1.19)` — el
   * pago aseador es sin IVA (persona natural) por definición.
   *
   * Devuelve SIEMPRE las 10 claves para que los consumidores (ingresoAf,
   * diagrama) no tengan que ramificar por preset.
   */
  const effectiveLimpiezaSubitems = useMemo(() => {
    const propAseador = n(range?.sueldoAseo);
    const propTranspTotal = n(range?.transporteTotal);
    const propTranspNeto = n(range?.transporteNeto);
    const propMaterTotal = n(range?.materialesTotal);
    const propMaterNeto = n(range?.materialesNeto);
    const propLavTotal = n(range?.lavanderiaTotal);
    const propLavNeto = n(range?.lavanderiaNeto);

    if (limpiezaPreset === "costo_0") {
      return {
        aseador: 0,
        transporteNeto: 0,
        transporteIva: 0,
        transporteTotal: 0,
        materialesNeto: 0,
        materialesIva: 0,
        materialesTotal: 0,
        lavanderiaNeto: 0,
        lavanderiaIva: 0,
        lavanderiaTotal: 0,
      };
    }

    if (limpiezaPreset === "sin_lavanderia") {
      return {
        aseador: propAseador,
        transporteNeto: propTranspNeto,
        transporteIva: Math.max(0, propTranspTotal - propTranspNeto),
        transporteTotal: propTranspTotal,
        materialesNeto: propMaterNeto,
        materialesIva: Math.max(0, propMaterTotal - propMaterNeto),
        materialesTotal: propMaterTotal,
        lavanderiaNeto: 0,
        lavanderiaIva: 0,
        lavanderiaTotal: 0,
      };
    }

    if (limpiezaPreset === "custom") {
      const aseador = n(limpiezaCustomAseador);
      const transTotal = n(limpiezaCustomTransporteTotal);
      const materTotal = n(limpiezaCustomMaterialesTotal);
      const lavTotal = n(limpiezaCustomLavanderiaTotal);
      // IVA → neto via netoFromConIva (centralizado en utils/iva.ts).
      // Aseador sin IVA.
      const transNeto = netoFromConIva(transTotal);
      const materNeto = netoFromConIva(materTotal);
      const lavNeto = netoFromConIva(lavTotal);
      return {
        aseador,
        transporteNeto: transNeto,
        transporteIva: Math.max(0, transTotal - transNeto),
        transporteTotal: transTotal,
        materialesNeto: materNeto,
        materialesIva: Math.max(0, materTotal - materNeto),
        materialesTotal: materTotal,
        lavanderiaNeto: lavNeto,
        lavanderiaIva: Math.max(0, lavTotal - lavNeto),
        lavanderiaTotal: lavTotal,
      };
    }

    // default — usa los subitems de la propiedad
    return {
      aseador: propAseador,
      transporteNeto: propTranspNeto,
      transporteIva: Math.max(0, propTranspTotal - propTranspNeto),
      transporteTotal: propTranspTotal,
      materialesNeto: propMaterNeto,
      materialesIva: Math.max(0, propMaterTotal - propMaterNeto),
      materialesTotal: propMaterTotal,
      lavanderiaNeto: propLavNeto,
      lavanderiaIva: Math.max(0, propLavTotal - propLavNeto),
      lavanderiaTotal: propLavTotal,
    };
  }, [
    limpiezaPreset,
    range,
    limpiezaCustomAseador,
    limpiezaCustomTransporteTotal,
    limpiezaCustomMaterialesTotal,
    limpiezaCustomLavanderiaTotal,
  ]);

  /**
   * Suma efectiva de los 4 subitems — usada para sincronizar `limpiezaClp`
   * cuando hay preset activo. El admin NO edita `limpiezaClp` directamente
   * mientras usa el desglose (es read-only §27), sigue siendo el
   * campo que persiste en DB y que manda al cálculo de reservaTotal.
   */
  const limpiezaFromSubitems =
    effectiveLimpiezaSubitems.aseador +
    effectiveLimpiezaSubitems.transporteTotal +
    effectiveLimpiezaSubitems.materialesTotal +
    effectiveLimpiezaSubitems.lavanderiaTotal;

  // Snapshot de los 4 subitems custom tomado al salir de "Personalizada",
  // para RESTAURARLOS si el admin vuelve. Espejo exacto del patrón del
  // desglose del ASEO (aseoCustomSnapshotRef, round 2026-08-02).
  const limpiezaCustomSnapshotRef = useRef<Record<string, number> | null>(null);
  useEffect(() => {
    if (!open) {
      limpiezaCustomSnapshotRef.current = null;
    }
  }, [open]);

  /**
   * Cambio MANUAL del "Modo" del desglose de limpieza (Select del Collapse).
   * Fix 2026-08-13 — vive en el onChange (evento inequívocamente iniciado
   * por el admin) y NO en un efecto reactivo. El efecto anterior corría en
   * el commit del montaje con el watch `limpiezaPreset` todavía en su
   * fallback "default" (stale) y PISABA los valores custom recién
   * hidratados con los subitems de la propiedad: un arriendo guardado con
   * desglose "Personalizada" se reabría mostrando (y re-guardando) los
   * valores por defecto de la propiedad. Misma clase de bug que el desglose
   * del aseo corrigió en el round 2026-08-02 (ver handleAseoPresetChange).
   */
  const handleLimpiezaPresetChange = (
    next: "default" | "sin_lavanderia" | "costo_0" | "custom",
  ) => {
    if (next === "custom") {
      // Volver a "Personalizada": restaurar lo tipeado antes del repintado.
      // Sin snapshot (primer cambio a custom) se conservan los valores
      // actuales — los del preset previo ya pintados en los inputs.
      if (limpiezaCustomSnapshotRef.current) {
        form.setFieldsValue(limpiezaCustomSnapshotRef.current);
        limpiezaCustomSnapshotRef.current = null;
      }
      return;
    }
    // Salida de "Personalizada": recordar lo tipeado para poder volver.
    // `limpiezaPreset` (closure del render) todavía trae el preset PREVIO
    // — el form ya tiene `next`, pero el watch aún no re-renderizó.
    if (limpiezaPreset === "custom") {
      limpiezaCustomSnapshotRef.current = form.getFieldsValue([
        "limpiezaCustomAseador",
        "limpiezaCustomTransporteTotal",
        "limpiezaCustomMaterialesTotal",
        "limpiezaCustomLavanderiaTotal",
      ]);
    }
    // Repintar los 4 inputs (quedan read-only) con los valores efectivos
    // del preset elegido. costo_0 → todo 0; sin_lavanderia → propiedad con
    // lavandería 0; default → propiedad completa.
    form.setFieldsValue({
      limpiezaCustomAseador: next === "costo_0" ? 0 : n(range?.sueldoAseo),
      limpiezaCustomTransporteTotal:
        next === "costo_0" ? 0 : n(range?.transporteTotal),
      limpiezaCustomMaterialesTotal:
        next === "costo_0" ? 0 : n(range?.materialesTotal),
      limpiezaCustomLavanderiaTotal:
        next === "default" ? n(range?.lavanderiaTotal) : 0,
    });
  };

  /**
   * Label legible del preset activo — se muestra en el header del Collapse
   * para que el admin sepa qué modo está activo SIN tener que expandir.
   */
  const limpiezaPresetLabel =
    limpiezaPreset === "sin_lavanderia"
      ? "Sin lavandería"
      : limpiezaPreset === "costo_0"
        ? "Costo 0 (gratis)"
        : limpiezaPreset === "custom"
          ? "Personalizada"
          : "Por defecto (propiedad)";

  /**
   * §27: siempre sincronizamos `limpiezaClp` con la suma de los subitems
   * efectivos, INCLUSO en preset "default". Así el input del header
   * siempre refleja el desglose y no queda desalineado si el admin cambia
   * de preset y vuelve.
   */
  useEffect(() => {
    if (kind !== "booking") return;
    // El admin eligió explícitamente que la limpieza sea 0 — persistimos
    // 0 (no undefined) para que la validación lo acepte y el cálculo de
    // reservaTotal lo use como 0 real.
    const explicitZero =
      limpiezaPreset === "costo_0" ||
      (limpiezaPreset === "custom" && limpiezaFromSubitems === 0);

    if (limpiezaFromSubitems > 0 || explicitZero) {
      form.setFieldsValue({ limpiezaClp: limpiezaFromSubitems });
      return;
    }
    // Suma 0 con preset default/sin_lavanderia: típicamente significa que
    // los subitems aún no se hidrataron. En create mode dejamos undefined
    // (para que el usuario entienda que tiene que elegir un modo); en edit
    // NO pisamos — preservamos el valor cargado (regresión 2026-04-23).
    if (mode === "create") {
      form.setFieldsValue({ limpiezaClp: undefined });
    }
  }, [limpiezaPreset, limpiezaFromSubitems, kind, form, mode]);

  /**
   * Pintado de los 4 inputs read-only del desglose — SOLO EN CREACIÓN.
   * Mantiene los inputs alineados con los subitems efectivos mientras el
   * preset no sea "custom" (p. ej. cuando `range` llega async con los
   * valores de la propiedad).
   *
   * Fix 2026-08-13 — en EDICIÓN no corre NUNCA: la hidratación siembra los
   * valores guardados y solo el onChange del Select los cambia
   * (handleLimpiezaPresetChange). La versión anterior corría también en
   * edición y, en el commit del montaje (watch `limpiezaPreset` aún en su
   * fallback "default"), pisaba el desglose "Personalizada" hidratado con
   * los valores de la propiedad. Mismo racional que el desglose del aseo
   * (round 2026-08-02).
   */
  useEffect(() => {
    if (!open || mode === "edit") return;
    if (kind !== "booking") return;
    if (limpiezaPreset === "custom") return;
    form.setFieldsValue({
      limpiezaCustomAseador: effectiveLimpiezaSubitems.aseador,
      limpiezaCustomTransporteTotal: effectiveLimpiezaSubitems.transporteTotal,
      limpiezaCustomMaterialesTotal: effectiveLimpiezaSubitems.materialesTotal,
      limpiezaCustomLavanderiaTotal: effectiveLimpiezaSubitems.lavanderiaTotal,
    });
  }, [open, mode, limpiezaPreset, effectiveLimpiezaSubitems, kind, form]);

  // Watchers para el diagrama del resumen (sólo lectura, no disparan autocalc).
  const reservaTotalClpWatch = Form.useWatch("reservaTotalClp", form);
  const reservaTotalPropietarioWatch = Form.useWatch(
    "reservaTotalPropietario",
    form,
  );
  const ingresoAfClpWatch = Form.useWatch("ingresoAfClp", form);
  const limpiezaClpWatch = Form.useWatch("limpiezaClp", form);
  const comisionPlataformaWatch = Form.useWatch("comisionPlataformaClp", form);

  // Ref para direccionalidad del autocálculo de precios.
  // null = sin edición reciente (al abrir el modal).
  // "noche" = el admin escribió "precio por noche" → derivamos "precio noches".
  // "noches" = el admin escribió "precio noches" (total) → derivamos "precio por noche".
  // Ver docs/modulo-arriendos-precios.md §Autocalculo bidireccional.
  const lastEditedPriceRef = useRef<"noche" | "noches" | null>(null);

  // Priming del ref en edit mode. Si el booking cargado trae un TOTAL
  // (`precioNochesClp`) — incluso si no trae precio/noche — la primera
  // corrida del autocalc DEBE back-solve desde el total para no pisar ese
  // total con `base × baseNights = 0 × N`. Sin este priming, bookings
  // antiguos que sólo guardaron el total quedan con el total a $0 al abrir
  // edición (regresión 2026-04-23).
  useEffect(() => {
    if (!open) {
      lastEditedPriceRef.current = null;
      return;
    }
    if (mode !== "edit" || !initialValues) return;
    const nocheVal = Number((initialValues as any).precioNocheClp) || 0;
    const nochesVal = Number((initialValues as any).precioNochesClp) || 0;
    if (nochesVal > 0 && nocheVal === 0) {
      lastEditedPriceRef.current = "noches";
    } else {
      lastEditedPriceRef.current = "noche";
    }
  }, [open, mode, initialValues]);

  useEffect(() => {
    if (kind !== "booking") return;
    const ingresoStr = ingreso
      ? typeof ingreso === "string"
        ? ingreso
        : ingreso.format("YYYY-MM-DD")
      : range!.startDate;
    // Si salida está vacía (caso single-day), dejamos noches=0 en vez de
    // caer a range.endDate — así el admin ve claramente que debe completar.
    const salidaStr = salida
      ? typeof salida === "string"
        ? salida
        : salida.format("YYYY-MM-DD")
      : null;
    const nochesCalc = salidaStr ? daysBetween(ingresoStr, salidaStr) : 0;

    // tasa AF: asume 0.1 (10%). Si viene como 10, lo convertimos a 0.10.
    let tasa = n(range?.tasaComisionAf);
    if (tasa > 1) tasa = tasa / 100;

    const extrasSin = normalizeMoneyLines(extrasSinComisionClp);
    const extrasCon = normalizeMoneyLines(extrasConComisionClp);

    // Autocalculo bidireccional precio por noche ↔ precio noches (total),
    // ahora con semántica de PROMEDIO PONDERADO cuando hay extensiones.
    //
    // Convenciones (§28.2):
    //   - `precioNocheClp` (input visible) = precio BASE por noche que el
    //     admin ingresa para las noches no-extensión. Es el único precio
    //     editable directamente desde ese campo.
    //   - `precioNochesClp` (total visible) = TOTAL PONDERADO =
    //       precioBase × baseNights + Σ(ext_noches_i × ext_precio_i).
    //   - baseNights = nochesCalc − Σ ext_noches (nochesCalc ya incluye las
    //     noches-extensión auto-agregadas a la `salida` por el efecto de
    //     auto-extensión).
    //   - El "promedio ponderado" = precioNochesClp / nochesCalc se expone
    //     como helper text debajo del input (UI informativa).
    //
    // Esto REEMPLAZA el bug anterior en el que `precioNoches = base × TOTAL`
    // y luego se sumaba `+ totalExtensionesNoches` por separado (las
    // noches-extensión quedaban contabilizadas 2 veces: al precio base y al
    // precio de la extensión).
    const lastEdited = lastEditedPriceRef.current;
    const precioNocheFromForm = n(precioNocheClp);
    const precioNochesFromForm = n(form.getFieldValue("precioNochesClp"));
    const baseNights = Math.max(0, nochesCalc - totalExtensionesNochesCount);

    let precioNoches: number;
    let precioNocheResolved: number = precioNocheFromForm;

    // §28.2 / §28.3 — La salida tardía también entra al "total ponderado de
    // noches": su monto se reparte por igual entre todas las noches del
    // arriendo (matemáticamente equivale a sumar el monto al total de noches
    // y luego dividir por `nochesCalc` para sacar el promedio).
    // Entonces el total ponderado REAL = base × baseNights + Σ ext + salida_tardia.
    if (lastEdited === "noches" && nochesCalc > 0 && precioNochesFromForm > 0) {
      // El admin editó el TOTAL ponderado directamente. Back-solve el precio
      // base para que `base × baseNights + Σ ext + salida_tardia = total`
      // siga cumpliéndose (la salida tardía aporta al total al igual que las
      // extensiones, repartida por igual entre todas las noches).
      precioNoches = roundPeso(precioNochesFromForm);
      if (baseNights > 0) {
        precioNocheResolved = roundPeso(
          Math.max(
            0,
            precioNochesFromForm - totalExtensionesNoches - totalSalidaTardia,
          ) / baseNights,
        );
      } else {
        // Todas las noches son extensiones — no hay base que derivar. El
        // total está gobernado íntegramente por los precios de extensión +
        // salida tardía, así que dejamos `precioNocheResolved` como el
        // promedio ponderado para mantener consistencia en el UI.
        precioNocheResolved =
          nochesCalc > 0
            ? roundPeso(precioNochesFromForm / nochesCalc)
            : precioNocheFromForm;
      }
    } else {
      // El admin editó el precio base por noche (o una extensión, o la
      // salida tardía, o nada). Total ponderado =
      //   base × baseNights + Σ ext_noches × ext_precio + salida_tardia_monto.
      // (La salida tardía NO tiene un campo "noches" — se reparte por igual
      // entre todas las noches, por eso sumarla directo al total equivale a
      // `(monto / nochesCalc) × nochesCalc`.)
      precioNoches = roundPeso(
        precioNocheFromForm * baseNights +
          totalExtensionesNoches +
          totalSalidaTardia,
      );
      precioNocheResolved = precioNocheFromForm;
    }

    // Promedio ponderado por noche (sólo para display / tooltip). Cuando no
    // hay extensiones ni salida tardía coincide con `precioNocheResolved`.
    const precioNochePromedio =
      nochesCalc > 0 && precioNoches > 0
        ? roundPeso(precioNoches / nochesCalc)
        : precioNocheResolved;

    // §28.3 (actualizado): la salida tardía ya NO se suma al bucket de
    // "extras con comisión". Se folda en el total ponderado de noches
    // (ver arriba — `precioNoches` ya la incluye, repartida por igual entre
    // todas las noches del arriendo). Esto mantiene el mismo `ingreso AF`
    // total (la comisión AF sobre la salida tardía ahora se acumula dentro
    // de `comAfNoches` en vez de `comAfExtras`) y además propaga la ganancia
    // al promedio ponderado del precio por noche — que es lo que el admin
    // ve en el UI como "$X / noche".
    const baseExtrasConComisionTotal = sumMoneyLines(extrasCon);

    const totalExtras = roundPeso(
      sumMoneyLines(extrasSin) + baseExtrasConComisionTotal,
    );

    // Descuentos con tipo (ver DiscountLines):
    //   - "ajuste_total": reduce precioNochesEfectivo → cascada en com AF noches.
    //   - "solo_af": pérdida exclusiva de AF, no afecta Cliente ni reserva total.
    // Si el admin mandó un número plano (legacy), lo tratamos como ajuste_total.
    const descRaw = Array.isArray(descuentoClp)
      ? descuentoClp
      : n(descuentoClp) > 0
        ? [{ name: "descuento", _f_val: n(descuentoClp), tipo: "ajuste_total" }]
        : [];
    const descAjuste = descRaw
      .filter((d: any) => (d?.tipo ?? "ajuste_total") === "ajuste_total")
      .reduce((acc: number, d: any) => acc + (Number(d?._f_val) || 0), 0);
    const descSoloAf = descRaw
      .filter((d: any) => d?.tipo === "solo_af")
      .reduce((acc: number, d: any) => acc + (Number(d?._f_val) || 0), 0);

    // §28.2: `precioNoches` YA es el total ponderado (base × baseNights +
    // Σ ext_noches × ext_precio). NO volvemos a sumar las extensiones — eso
    // era el bug de doble conteo. Aplicamos el descuento ajuste_total sobre
    // el total ponderado para obtener el precioNochesEfectivo.
    const precioNochesEfectivo = Math.max(0, precioNoches - descAjuste);

    // Fix 2026-08-13 — semántica de `precioNoches` según ORIGEN del arriendo:
    //   - Manual ("Directa", con o sin comisión de plataforma tipeada): el
    //     admin ingresa el precio PUBLICADO por noche → `precioNoches` es
    //     BRUTO (incluye lo que la plataforma retiene).
    //   - API (Airbnb/Booking/Despegar/Stays): `precio_noches_clp` se
    //     persiste como residuo YA NETO de comisión de plataforma
    //     (helpers.js: total − plataforma − limpieza − extras).
    // La comisión AF se calcula SIEMPRE sobre las noches NETAS de comisión
    // de plataforma (flujo-financiero.md §4 — el dinero que la plataforma
    // retiene nunca llega, no puede devengar comisión). Antes el camino
    // manual comisionaba sobre el BRUTO: un arriendo de plataforma cargado
    // a mano cobraba comisión AF de más (y el mismo arriendo entrado por
    // API, de menos en reservaTotal al editarlo). En un arriendo directo
    // sin plataforma, comisión = 0 y ambas bases coinciden.
    const comPlat = Math.max(0, n(comisionPlataformaClp));
    const esOrigenApi =
      mode === "edit" &&
      ((initialValues as any)?.nombrePlataforma ?? "Directa") !== "Directa";
    // Base de la comisión AF: noches netas de comisión de plataforma.
    const baseComisionNoches = esOrigenApi
      ? precioNochesEfectivo
      : Math.max(0, precioNochesEfectivo - comPlat);
    // Noches BRUTAS (lo que el huésped pagó por las noches, incluida la
    // parte que retiene la plataforma) — alimentan la reserva total.
    const nochesBrutas = esOrigenApi
      ? precioNochesEfectivo + comPlat
      : precioNochesEfectivo;

    // Comisiones AF sobre la base NETA de noches (si hubo descuento
    // ajuste_total, la base baja). Incluye noches base + extensiones +
    // salida tardía (§28.3 — la salida tardía ahora se reparte por igual
    // entre todas las noches, así que devenga comisión dentro de este cubo,
    // no en `comAfExtras`).
    const comAfNoches = roundPeso(baseComisionNoches * tasa);
    // Extras con comisión (§28.3: YA no incluye salida tardía).
    const comAfExtras = roundPeso(baseExtrasConComisionTotal * tasa);

    const ivaAfNoches = roundPeso(comAfNoches * getIvaRate());
    const ivaAfExtras = roundPeso(comAfExtras * getIvaRate());

    // Reserva total: lo que paga el huésped en total.
    // Fórmula: noches BRUTAS + limpieza + extras (la comisión de plataforma
    // viaja DENTRO de las noches brutas: el huésped la paga junto con todo).
    // Los descuentos tipo ajuste_total ya están aplicados sobre precioNochesEfectivo.
    // Los descuentos tipo solo_af NO reducen la reserva total (el huésped paga lo mismo).
    const reservaTotal = roundPeso(
      nochesBrutas + n(limpiezaClp) + totalExtras,
    );

    // Ingreso AF — lo que gana Aloja Fácil empresa POR EL ARRIENDO
    // (fórmula canónica de flujo-financiero.md §4, espejo de
    // `computeIngresoAfClp` en backend/src/utils/helpers.js):
    //   + comisiones AF sobre noches y extras
    //   + IVA de esas comisiones
    //   + "otros cargos" con rol_af='recibe' (suma), 'paga' (resta),
    //     'intermediario' (neto 0). Ver flujo-financiero.md §10.
    //   − descuentos 'solo_af' (pérdida exclusiva AF, §9).
    // NO incluye NINGÚN subítem de limpieza (transporte / materiales /
    // lavandería / pago aseador): esos viven dentro de "Limpieza" y se
    // contabilizan en sus pestañas dedicadas (/finanzas-empresa → Aseos y
    // → Lavandería). Fix 2026-07-03: esta fórmula los sumaba (versión
    // pre-2026-05-13) y la card "GANANCIA DE ALOJA FÁCIL" + el diagrama
    // de repartición mostraban un valor inflado (ej. 38.800 en vez de
    // 23.800) que no cuadraba con lo persistido ni con los paneles.

    // Otros cargos: aplicar rol_af según flujo-financiero.md §10.
    // "intermediario" aporta 0 al neto, pero conceptualmente implica un
    // +monto de ingreso Y un -monto de egreso (dos entradas en el
    // ledger). Acá sólo trackeamos el neto — el ledger de AF se
    // construye en backend.
    const otrosCargosNetoAf = (
      Array.isArray(otrosCargosWatch) ? otrosCargosWatch : []
    ).reduce((acc: number, c: any) => {
      const monto = Number(c?.monto_clp) || 0;
      const rol = c?.rol_af;
      if (rol === "recibe") return acc + monto;
      if (rol === "paga") return acc - monto;
      return acc; // intermediario → 0
    }, 0);

    const ingresoAf = roundPeso(
      comAfNoches + comAfExtras + ivaAfNoches + ivaAfExtras + otrosCargosNetoAf - descSoloAf,
    );

    // Ingreso Cliente — lo que recibe el Cliente después de descontar TODO
    // lo que no le corresponde (plataforma, limpieza completa, comisiones AF + IVAs).
    // La limpieza se descuenta ENTERA (incluye IVAs internos) porque el Cliente
    // paga toda la limpieza; los subitems se redistribuyen a aseador/AF/lav AF.
    const ingresoCliente = roundPeso(
      reservaTotal -
        n(comisionPlataformaClp) -
        n(limpiezaClp) -
        comAfNoches -
        ivaAfNoches -
        comAfExtras -
        ivaAfExtras,
    );

    // Helper para campos numéricos editables: evita pisar valores cargados
    // en edit mode cuando el cálculo da 0 (caso: booking antiguo sin
    // precio_por_noche pero con total) y evita pintar "$0" en create mode
    // (bug "017892": si el campo arranca en 0 y el admin escribe "17892",
    // AntD renderiza "017892" antes de normalizar). En create con 0 →
    // undefined (el placeholder muestra "0"); en edit con 0 → preservamos
    // lo que ya venía.
    const writeEditable = (
      computed: number,
      fieldName: string,
    ): number | undefined => {
      if (computed > 0) return computed;
      if (mode === "create") return undefined;
      return form.getFieldValue(fieldName);
    };

    form.setFieldsValue({
      noches: nochesCalc,
      tasaComisionAf: tasa,

      // Autocalculo bidireccional: reescribimos ambos para mantener consistencia.
      // `precioNocheClp` = precio BASE por noche (no-extensión).
      // `precioNochesClp` = TOTAL ponderado (base × baseNights + Σ ext × precio_ext).
      // `precioNochePromedioClp` = promedio ponderado (solo display — no se persiste).
      precioNocheClp: writeEditable(precioNocheResolved, "precioNocheClp"),
      precioNochesClp: writeEditable(precioNoches, "precioNochesClp"),
      precioNochePromedioClp: writeEditable(
        precioNochePromedio,
        "precioNochePromedioClp",
      ),
      totalExtrasClp: writeEditable(totalExtras, "totalExtrasClp"),

      // impuestoPlataformaClp ya NO se calcula — la comisión plataforma incluye IVA.

      comisionAfNochesClp: comAfNoches,
      comisionAfExtrasClp: comAfExtras,
      ivaComisionDeNochesClp: ivaAfNoches,
      ivaComisionDeExtrasClp: ivaAfExtras,

      reservaTotalClp: reservaTotal,
      // reservaTotalPropietario es la columna DB histórica — en UI es "Ingreso Cliente".
      reservaTotalPropietario: ingresoCliente,
      ingresoAfClp: ingresoAf,

      // Plataforma / moneda / inmutable: estos campos describen el ORIGEN
      // del arriendo y solo tienen sentido fijarlos al CREAR desde el
      // calendario (entrada manual → "Directa" / "CLP" / no inmutable).
      // En edición se preservan los del arriendo original — si el booking
      // vino de Airbnb / Booking / Stays, forzar "Directa" en el autocalc
      // pisaba el origen verdadero. Action sí lo seteamos en edit para
      // dejar trazabilidad de la última operación.
      ...(mode === "edit"
        ? { action: "edicion" }
        : {
            moneda: "CLP",
            nombrePlataforma: "Directa",
            idPlataforma: "directa",
            action: "creacion",
            inmutable: false,
          }),
    });
  }, [
    kind,
    ingreso,
    salida,
    precioNocheClp,
    precioNochesClp,
    limpiezaClp,
    comisionPlataformaClp,
    extrasSinComisionClp,
    extrasConComisionClp,
    descuentoClp,
    range,
    form,
    // Fix 2026-08-13 — el origen del arriendo (Directa vs API) decide si
    // `precioNoches` es bruto o neto de comisión plataforma. Ambos son
    // estables durante una sesión del modal.
    mode,
    initialValues,
    // Cambios en el desglose de limpieza (preset o custom subitems)
    // también tienen que re-disparar el autocalc para refrescar ingresoAf.
    effectiveLimpiezaSubitems,
    // §28: cargos adicionales (extensiones de noches + salida tardía)
    // afectan reservaTotal y comisiones. "Otros" SÍ entra al autocalc
    // ahora: su neto por rol_af (recibe/paga/intermediario) impacta
    // ingreso AF (doc flujo-financiero.md §10).
    totalExtensionesNoches,
    totalExtensionesNochesCount,
    totalSalidaTardia,
    otrosCargosWatch,
  ]);

  // ─── Default de "precio de cada noche" = promedio del precio base ──────────
  // Cuando un admin crea un arriendo y elige las fechas, prellenamos el "precio
  // de cada noche" con el PROMEDIO de los precios base (CLP) que el calendario
  // de Stays tiene para esas noches: Σ base[noche] / nº de noches. Así, si
  // distintas noches tienen distinto precio base, el default queda ponderado.
  //
  // Reglas:
  //  - Solo en modo CREATE + arriendo (booking).
  //  - Solo si el admin AÚN no tocó manualmente el precio (lastEditedPriceRef
  //    sigue en null) — nunca pisamos lo que el admin escribió.
  //  - Se recalcula si cambian las fechas (ingreso/salida), mientras el admin
  //    no haya editado el precio a mano.
  //  - Si la propiedad no tiene listing de Stays o no hay precios efectivos para
  //    esas noches, no hacemos nada (el admin lo ingresa manualmente).
  //
  // Reutiliza el endpoint de meta del calendario (calendarData.efectivo =
  // { [YYYY-MM-DD]: clp } = base + condición), así funciona sin depender de que
  // la propiedad esté expandida en la grilla.
  useEffect(() => {
    if (!open || mode !== "create" || kind !== "booking") return;
    const propertyId = range?.propertyId;
    if (propertyId == null) return;

    const ingresoStr = ingreso
      ? dayjs.isDayjs(ingreso)
        ? ingreso.format("YYYY-MM-DD")
        : String(ingreso)
      : null;
    const salidaStr = salida
      ? dayjs.isDayjs(salida)
        ? salida.format("YYYY-MM-DD")
        : String(salida)
      : null;
    if (!ingresoStr || !salidaStr) return;
    if (daysBetween(ingresoStr, salidaStr) <= 0) return;

    // No pisar lo que el admin ya escribió a mano.
    if (lastEditedPriceRef.current !== null) return;

    let cancelled = false;
    (async () => {
      const data = await apiFetch<any>(
        `/stays/calendario/meta/${propertyId}?from=${encodeURIComponent(
          ingresoStr,
        )}&to=${encodeURIComponent(salidaStr)}`,
      );
      if (cancelled) return;
      // EFECTIVO por noche (= base + condición aplicada) — es lo que realmente
      // se cobra al huésped y con lo que SIEMPRE debe calcularse un arriendo.
      // Sin condición, efectivo === base; con condición (±%, ±monto) el efectivo
      // es el precio ajustado. Bug 2026-07-20: antes sembraba `base`, ignorando
      // la condición (p.ej. base $100.000 +50% → efectivo $150.000, pero el
      // arriendo se creaba con $100.000). `efectivo` y `base` comparten fechas
      // (overlayPreciosNoche); el fallback per-noche a `base` cubre cualquier
      // fecha sin efectivo (que además sería igual al base). Ver precios.service.js.
      const efectivo: Record<string, unknown> = data?.calendarData?.efectivo ?? {};
      const base: Record<string, unknown> = data?.calendarData?.base ?? {};

      // Promedio sobre las noches del arriendo: intervalo semiabierto
      // [ingreso, salida) — la noche de salida NO se cobra. Solo promediamos
      // las noches con precio efectivo conocido (> 0).
      let suma = 0;
      let cuenta = 0;
      let cursor = dayjs(ingresoStr);
      const fin = dayjs(salidaStr);
      while (cursor.isBefore(fin)) {
        const key = cursor.format("YYYY-MM-DD");
        const precio = Number(efectivo[key] ?? base[key]);
        if (Number.isFinite(precio) && precio > 0) {
          suma += precio;
          cuenta += 1;
        }
        cursor = cursor.add(1, "day");
      }
      if (cuenta === 0) return; // sin precios base conocidos → dejar manual

      const promedio = roundPeso(suma / cuenta);
      if (promedio <= 0) return;

      // Re-chequear tras el await: el admin pudo escribir un precio o cambiar
      // las fechas mientras llegaba la respuesta.
      if (cancelled || lastEditedPriceRef.current !== null) return;
      // No marcamos lastEditedPriceRef: dejamos que el autocalc derive el total
      // desde este precio base (rama "noche") y que un cambio de fechas vuelva
      // a recomputar el promedio.
      form.setFieldsValue({ precioNocheClp: promedio });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, kind, range?.propertyId, ingreso, salida]);

  useEffect(() => {
    if (kind !== "block" && kind !== "maintenance") return;
    if (!open) return;

    // Recalcula noches en base a los pickers de fecha actuales del form
    // (si los cambia el admin, noches se ajusta).
    const iVal = form.getFieldValue("ingreso");
    const sVal = form.getFieldValue("salida");
    const iStr = iVal
      ? dayjs.isDayjs(iVal)
        ? iVal.format("YYYY-MM-DD")
        : iVal
      : range?.startDate;
    const sStr = sVal
      ? dayjs.isDayjs(sVal)
        ? sVal.format("YYYY-MM-DD")
        : sVal
      : range?.endDate;
    if (iStr && sStr) {
      form.setFieldsValue({ noches: daysBetween(iStr, sStr) });
    }
  }, [open, range, kind, form, ingreso, salida]);

  /**
   * Cuando admin cambia el tipo en el Select a block, maintenance o
   * booking en modo create, reseteamos los defaults horarios al esquema
   * canónico del dominio:
   *   - Tercios: evening → morning
   *   - Horas:   15:00  → 10:59
   *   - Salida:  ingreso + 1 día (si estaba en el mismo día)
   *
   * Justificación: bloqueos, mantenciones y estadías comparten el mismo
   * modelo día-a-día (ver docs/calendario-logica.md §14) — entran a las
   * 15:00, salen a las 10:59. Al cambiar entre estos tipos la UI
   * mantiene el schema; si el admin viene de aseo (mid/mid), reseteamos.
   *
   * NO corre en edit-mode para no sobreescribir valores existentes.
   */
  useEffect(() => {
    if (mode !== "create") return;
    if (!open) return;
    // BUG FIX 2026-04: leíamos `kind` via Form.useWatch, que propaga de
    // forma async. Durante el PRIMER pase de effects tras abrir el modal,
    // el useEffect de create (línea ~415) ya setea `kind` en form state,
    // pero el valor watched todavía es el inicial ("cleaning"). Eso hacía
    // que este efecto se saltara en el primer pase y, peor, que el efecto
    // de cleaning de abajo corriera y colapsara `salida` a `ingreso`. En
    // la re-render, este efecto detectaba salida===ingreso y hacía +1 día,
    // resultando en salida = ingreso + 1 SIEMPRE, ignorando la segunda
    // fecha clickeada por el admin en el calendario. Leemos directo del
    // form state para evitar el desfase.
    const currentKind = form.getFieldValue("kind") ?? kind;
    if (
      currentKind !== "block" &&
      currentKind !== "maintenance" &&
      currentKind !== "booking"
    )
      return;
    // Si la apertura traía un preset (p.ej. "Día completo" con
    // range.presetKind="block" + horas 00:00/23:59) y el kind actual
    // todavía coincide con el preset, no pisamos los valores iniciales.
    // En cuanto el admin cambie kind a otro valor, esta guarda deja de
    // aplicar y los defaults canónicos se re-aplican.
    if (
      range?.presetKind === currentKind &&
      (range?.presetStartTime || range?.presetEndTime)
    ) {
      return;
    }

    form.setFieldsValue({
      startThird: "evening" as Third,
      endThird: "morning" as Third,
      startTime: dayjs("15:00", "HH:mm"),
      endTime: dayjs("10:59", "HH:mm"),
    });

    const currentIngreso = form.getFieldValue("ingreso");
    const currentSalida = form.getFieldValue("salida");
    const i = dayjs.isDayjs(currentIngreso)
      ? currentIngreso
      : currentIngreso
        ? dayjs(currentIngreso)
        : null;
    const s = dayjs.isDayjs(currentSalida)
      ? currentSalida
      : currentSalida
        ? dayjs(currentSalida)
        : null;
    if (i && (!s || i.isSame(s, "day"))) {
      form.setFieldsValue({ salida: i.add(1, "day") });
    }
  }, [kind, mode, open, form, range]);

  /**
   * Cuando el admin cambia el Select de tipo a "Aseo" en modo create,
   * reseteamos los defaults al esquema canónico del dominio:
   *   - Tercios: mid → mid
   *   - Horas:   11:00 → 14:59 (primer y último minuto del tercio mid)
   *   - Salida:  igual a ingreso (default intra-día — el admin puede
   *              cambiarla después si necesita un aseo multi-día)
   *
   * Sin esto, al cambiar desde booking o block el tercio y las horas
   * quedaban en los valores previos (ej. evening / 15:00) aunque el
   * tipo ahora es "aseo" — visualmente inconsistente y el admin debía
   * ajustar a mano. El patrón es idéntico al del bloque block/maintenance
   * de más arriba.
   */
  useEffect(() => {
    if (mode !== "create") return;
    if (!open) return;
    // BUG FIX 2026-04: ver comentario del useEffect anterior. Leemos el
    // kind directo desde el form state porque `Form.useWatch` devuelve
    // "cleaning" (initialKind) en el primer pase aunque el create-effect
    // ya haya seteado kind="booking". Sin esta guarda, al abrir el modal
    // con una selección multi-día (arriendo), este efecto colapsaba
    // `salida` a `ingreso`; después el efecto de booking le sumaba 1 día,
    // dejando la salida siempre un día después del ingreso — ignorando
    // la segunda fecha que el admin había clickeado en la grilla.
    const currentKind = form.getFieldValue("kind") ?? kind;
    if (currentKind !== "cleaning") return;
    // Misma guarda que en el useEffect de block/booking: si hay preset
    // activo que coincide con el kind actual, no pisamos. (Caso borde:
    // alguien abre "Día completo" con presetKind=cleaning — no es el
    // uso habitual, pero respetamos la intención.)
    if (
      range?.presetKind === currentKind &&
      (range?.presetStartTime || range?.presetEndTime)
    ) {
      return;
    }

    form.setFieldsValue({
      startThird: "mid" as Third,
      endThird: "mid" as Third,
      startTime: dayjs("11:00", "HH:mm"),
      endTime: dayjs("14:59", "HH:mm"),
    });

    // Aseo es intra-día: salida == ingreso. Si el admin venía de un
    // rango multi-día (ej. booking evening→morning), colapsamos salida
    // al mismo día que ingreso.
    const currentIngreso = form.getFieldValue("ingreso");
    const i = dayjs.isDayjs(currentIngreso)
      ? currentIngreso
      : currentIngreso
        ? dayjs(currentIngreso)
        : null;
    if (i) {
      form.setFieldsValue({ salida: i });
    }
  }, [kind, mode, open, form, range]);

  /**
   * Re-sincroniza `startTime` cuando el tercio inicial cambia. Si la hora
   * actual cae fuera del rango del nuevo tercio, la movemos al PRIMER
   * minuto del tercio (00:00 morning / 11:00 mid / 15:00 evening). Análogo
   * para `endTime` → ÚLTIMO minuto ocupado (10:59 / 14:59 / 23:59).
   *
   * Justificación: el admin cambia el tercio desde el Select, y
   * `disabledHours`/`disabledMinutes` del TimePicker sólo restringe lo que
   * el usuario PUEDE elegir, no lo que ya está seleccionado. Sin este
   * efecto, un rango previamente 15:00 (evening) + switch a mid podía
   * quedar con startTime=15:00 aunque mid termina en 14:59, rompiendo la
   * validación del onOk silenciosamente.
   */
  useEffect(() => {
    if (!open) return;
    const current = form.getFieldValue("startTime");
    if (!dayjs.isDayjs(current) || !current.isValid()) {
      form.setFieldsValue({
        startTime: dayjsOrDefaultTime(thirdToTimeStart(startThirdWatch).slice(0, 5)),
      });
      return;
    }
    const min = current.hour() * 60 + current.minute();
    const ranges = {
      morning: { min: 0, max: 10 * 60 + 59 },
      mid: { min: 11 * 60, max: 14 * 60 + 59 },
      evening: { min: 15 * 60, max: 23 * 60 + 59 },
    };
    const r = ranges[startThirdWatch];
    if (min < r.min || min > r.max) {
      form.setFieldsValue({
        startTime: dayjsOrDefaultTime(thirdToTimeStart(startThirdWatch).slice(0, 5)),
      });
    }
  }, [startThirdWatch, open, form]);

  useEffect(() => {
    if (!open) return;
    const current = form.getFieldValue("endTime");
    if (!dayjs.isDayjs(current) || !current.isValid()) {
      form.setFieldsValue({
        endTime: dayjsOrDefaultTime(thirdToTimeEnd(endThirdWatch).slice(0, 5)),
      });
      return;
    }
    const min = current.hour() * 60 + current.minute();
    const ranges = {
      morning: { min: 0, max: 10 * 60 + 59 },
      mid: { min: 11 * 60, max: 14 * 60 + 59 },
      evening: { min: 15 * 60, max: 23 * 60 + 59 },
    };
    const r = ranges[endThirdWatch];
    if (min < r.min || min > r.max) {
      form.setFieldsValue({
        endTime: dayjsOrDefaultTime(thirdToTimeEnd(endThirdWatch).slice(0, 5)),
      });
    }
  }, [endThirdWatch, open, form]);

  const kindTitle =
    kind === "cleaning"
      ? "Aseo"
      : kind === "booking"
        ? "Arriendo"
        : kind === "block"
          ? "Bloqueo"
          : "Mantención";

  const buildPayload = (values: any): CreatePayload => {
    const rawIngreso = values.ingreso;
    const rawSalida = values.salida;
    const asDateStr = (v: any, fallback: string): string => {
      if (!v) return fallback;
      if (typeof v === "string") return v;
      return v.format("YYYY-MM-DD");
    };
    // Los 4 kinds (booking, block, maintenance, cleaning) exponen
    // DatePickers de ingreso/salida, así que la fuente de verdad del
    // rango es siempre el form. Si por algún motivo el campo viene
    // vacío caemos al range (click original) como fallback defensivo.
    const startDate: string = asDateStr(rawIngreso, range!.startDate);
    const endDate: string = asDateStr(rawSalida, range!.endDate);

    const startTime: Dayjs = values.startTime;
    const endTime: Dayjs = values.endTime;

    const startHHMM = startTime.format("HH:mm");
    const endHHMM = endTime.format("HH:mm");

    const startThird: Third = values.startThird;
    const endThird: Third = values.endThird;

    const startISO = toIsoWithSeconds(startDate, startHHMM, false); // ...:00
    const endISO = toIsoWithSeconds(endDate, endHHMM, true); // ...:59

    const action = mode === "edit" ? "edicion" : "creacion";
    const id = mode === "edit" ? initialValues?.id : undefined;

    const base: CreatePayload = {
      kind: values.kind,
      id,
      action,
      propertyId: range!.propertyId,
      startDate,
      startThird,
      startTime: startHHMM,
      endDate,
      endThird,
      endTime: endHHMM,
      startISO,
      endISO,
    };

    if (values.kind === "cleaning") {
      // Fallback degradado (round 2026-08-02): si el detalle del aseo NO se
      // pudo cargar al abrir (calendario.tsx siembra aseoDetalleIncompleto
      // cuando fetchEventDetail falla) y el admin no tocó el Modo del
      // desglose, se OMITEN las claves financieras del desglose — el backend
      // (protección isUpdate) preserva lo que haya en DB. Sin esto, el preset
      // "default" sembrado por el fallback mandaba `subitemsOverride: null`
      // explícito y disparaba un RESET financiero del arriendo que el admin
      // nunca pidió (solo quería, p. ej., mover la fecha).
      const desgloseNoConfiable =
        mode === "edit" &&
        (initialValues as any)?.aseoDetalleIncompleto === true &&
        !form.isFieldTouched("aseoCostPreset");
      // Igual que el desglose financiero de arriba: si el detalle NO se pudo
      // cargar (aseoDetalleIncompleto), el editor cayó a "abierto" POR DEFECTO
      // — no porque el admin quisiera desasignar. Mandar `idAseador: null` ahí
      // BORRABA la asignación real con solo mover la fecha (bug reportado
      // 2026-09-09, destapado por el loop de reconexión WS que hacía fallar el
      // fetch del detalle). Si el admin NO tocó la asignación, se OMITE
      // idAseador/asignaciones y el backend la preserva (protección isUpdate de
      // buildBackendPayloadByKind). Si SÍ la tocó (eligió aseadora o marcó
      // "abierto" a propósito), viaja normal.
      const asignacionNoConfiable =
        mode === "edit" &&
        (initialValues as any)?.aseoDetalleIncompleto === true &&
        !form.isFieldTouched("modoAsignacion") &&
        !form.isFieldTouched("idAseador");
      return {
        ...base,
        modoAsignacion: values.modoAsignacion,
        ...(asignacionNoConfiable
          ? {}
          : {
              // Modo "abierto" = sin aseadora: NO viajan idAseador ni asignaciones
              // (un reparto rezagado en el form re-asignaría en silencio). Round
              // 2026-08-25 (equipo 1..N con reparto del pago).
              idAseador: values.modoAsignacion === "abierto" ? null : values.idAseador,
              nombreAseador:
                values.modoAsignacion === "abierto" ? null : values.nombreAseador,
            }),
        ...(!asignacionNoConfiable &&
        values.modoAsignacion !== "abierto" &&
        values.repartoTocado === true &&
        Array.isArray(values.asignaciones) &&
        values.asignaciones.length > 0
          ? {
              // Con `desgloseNoConfiable` el form se sembró con un sueldo
              // ADIVINADO (el fetch del detalle falló): mandar montos
              // calculados sobre ese total hacía que el backend los validara
              // contra el sueldo REAL de la DB y rechazara con 400 — el
              // admin no podía cambiar el equipo desde ese estado (review
              // 2026-09-07). Solución: viajan solo los ids y el backend
              // reparte en partes iguales del total verdadero.
              asignaciones: desgloseNoConfiable
                ? values.asignaciones.map((a: any) => ({ usuario_id: a.usuario_id }))
                : values.asignaciones,
            }
          : {}),
        costoLimpieza: desgloseNoConfiable ? undefined : values.costoLimpieza,
        sueldoAseador: desgloseNoConfiable ? undefined : values.sueldoAseador,
        horaInicio: values.horaInicio,
        urgencia: values.urgencia,
        estado: values.estado,
        // Round 2026-05-13 — el aseo ya no lleva `rolAF` ni `pagado_a`.
        // Solo `pagado_por` (Quién paga). Destinatarios implícitos.
        pagado_por: values.pagado_por,
        observaciones: values.observaciones,
        // §24.2 — Override del desglose del aseo. Solo persistimos cuando
        // el admin eligió "Personalizada"; en "Por defecto" el backend
        // guarda null y el aseo sigue heredando los subitems de la propiedad.
        subitemsOverride: desgloseNoConfiable
          ? undefined
          : values.aseoCostPreset === "custom"
            ? {
                preset: "custom",
                aseador: n(values.aseoCustomAseador),
                transporte_total: n(values.aseoCustomTransporteTotal),
                materiales_total: n(values.aseoCustomMaterialesTotal),
                lavanderia_total: n(values.aseoCustomLavanderiaTotal),
                // Netos e IVAs derivados via netoFromConIva (utils/iva.ts).
                // Aseador sin IVA.
                transporte_neto: netoFromConIva(
                  n(values.aseoCustomTransporteTotal),
                ),
                transporte_iva:
                  n(values.aseoCustomTransporteTotal) -
                  netoFromConIva(n(values.aseoCustomTransporteTotal)),
                materiales_neto: netoFromConIva(
                  n(values.aseoCustomMaterialesTotal),
                ),
                materiales_iva:
                  n(values.aseoCustomMaterialesTotal) -
                  netoFromConIva(n(values.aseoCustomMaterialesTotal)),
                lavanderia_neto: netoFromConIva(
                  n(values.aseoCustomLavanderiaTotal),
                ),
                lavanderia_iva:
                  n(values.aseoCustomLavanderiaTotal) -
                  netoFromConIva(n(values.aseoCustomLavanderiaTotal)),
              }
            : null,
      } as any;
    }

    if (values.kind === "block" || values.kind === "maintenance") {
      return {
        ...base,
        razon: values.razon,
        notas: values.notas,
        noches: values.noches,
      };
    }

    // booking
    // En modo edición preservamos plataforma/moneda del arriendo
    // original. El modal está pensado para creación manual desde el
    // calendario (de ahí los defaults "Directa" / "CLP"), pero si se
    // usa para EDITAR un arriendo que llegó por API (Airbnb / Booking /
    // Stays), forzar "Directa" en el submit destruye el origen real.
    // Cuando estamos en edit, no mandamos estos 3 campos → el backend
    // (Sequelize.update con la rama isUpdate) ignora keys ausentes y
    // conserva los valores previos.
    const overridesOrigen =
      mode === "edit"
        ? {}
        : {
            moneda: "CLP" as const,
            nombrePlataforma: "Directa" as const,
            idPlataforma: "directa" as const,
          };

    return {
      ...base,

      ingreso: startDate,
      salida: endDate,
      nombreHuesped: values.nombreHuesped,
      telefonoHuesped: values.telefonoHuesped,
      cantidadHuespedes: values.cantidadHuespedes,
      fechaCreacion: values.fechaCreacion
        ? dayjs.isDayjs(values.fechaCreacion)
          ? values.fechaCreacion.toISOString()
          : values.fechaCreacion
        : undefined,
      estado: values.estado,
      noches: values.noches,

      ...overridesOrigen,

      precioNocheClp: values.precioNocheClp,
      precioNochesClp: values.precioNochesClp,

      limpiezaClp: values.limpiezaClp,
      comisionPlataformaClp: values.comisionPlataformaClp,

      // Desglose de limpieza per-arriendo. Solo enviamos el JSONB cuando
      // el admin eligió un preset distinto a "default" — el backend en
      // ese caso persiste null y la reserva queda atada a la propiedad.
      limpiezaSubitemsOverride:
        values.limpiezaPreset && values.limpiezaPreset !== "default"
          ? {
              preset: values.limpiezaPreset,
              aseador: effectiveLimpiezaSubitems.aseador,
              transporte_neto: effectiveLimpiezaSubitems.transporteNeto,
              transporte_iva: effectiveLimpiezaSubitems.transporteIva,
              transporte_total: effectiveLimpiezaSubitems.transporteTotal,
              materiales_neto: effectiveLimpiezaSubitems.materialesNeto,
              materiales_iva: effectiveLimpiezaSubitems.materialesIva,
              materiales_total: effectiveLimpiezaSubitems.materialesTotal,
              lavanderia_neto: effectiveLimpiezaSubitems.lavanderiaNeto,
              lavanderia_iva: effectiveLimpiezaSubitems.lavanderiaIva,
              lavanderia_total: effectiveLimpiezaSubitems.lavanderiaTotal,
            }
          : null,

      // §28 — Cargos adicionales. El backend filtra lo vacío, pero acá
      // ya empaquetamos la estructura canónica para no tener que
      // reinterpretar nada en el server.
      adicionalesArriendo: (() => {
        const extensiones = Array.isArray(values.extensionesNoches)
          ? values.extensionesNoches
              .map((e: any) => ({
                noches: Number(e?.noches) || 0,
                precio_por_noche_clp: Number(e?.precio_por_noche_clp) || 0,
              }))
              .filter((e: any) => e.noches > 0)
          : [];
        const salidaTardia =
          values.salidaTardia && Number(values.salidaTardia.monto_clp) > 0
            ? {
                monto_clp: Number(values.salidaTardia.monto_clp) || 0,
                hora_nueva: values.salidaTardia.hora_nueva ?? undefined,
              }
            : null;
        const otros = Array.isArray(values.otrosCargos)
          ? values.otrosCargos
              .map((o: any) => ({
                nombre: String(o?.nombre ?? "").trim(),
                monto_clp: Number(o?.monto_clp) || 0,
                rol_af: o?.rol_af ?? "paga",
                pagado_por: o?.pagado_por ?? null,
                pagado_a: o?.pagado_a ?? null,
              }))
              .filter((o: any) => o.nombre && o.monto_clp > 0)
          : [];
        if (extensiones.length === 0 && !salidaTardia && otros.length === 0) {
          return null;
        }
        return {
          extensiones_noches: extensiones,
          salida_tardia: salidaTardia,
          otros,
        };
      })(),

      extrasSinComisionClp: normalizeMoneyLines(values.extrasSinComisionClp),
      extrasConComisionClp: normalizeMoneyLines(values.extrasConComisionClp),
      totalExtrasClp: values.totalExtrasClp,

      descuentoClp: values.descuentoClp,

      // impuestoPlataformaClp eliminado: la comisión plataforma ya incluye IVA.

      tasaComisionAf: values.tasaComisionAf,
      comisionAfNochesClp: values.comisionAfNochesClp,
      comisionAfExtrasClp: values.comisionAfExtrasClp,
      ivaComisionDeNochesClp: values.ivaComisionDeNochesClp,
      ivaComisionDeExtrasClp: values.ivaComisionDeExtrasClp,

      reservaTotalClp: values.reservaTotalClp,
      reservaTotalPropietario: values.reservaTotalPropietario,
      ingresoAfClp: values.ingresoAfClp,

      estadoPago: values.estadoPago,
      tipoPago: values.tipoPago,

      tipo: values.tipo ?? "reservado",
      notaInterna: values.notaInterna,

      // Solo en CREACIÓN: un arriendo nuevo nace mutable. En EDICIÓN se omite
      // la clave (el backend con isUpdate ignora keys ausentes) — mandar
      // `inmutable: false` acá "desmarcaba" silenciosamente el cierre de mes
      // de cualquier arriendo editado desde el calendario (round 2026-08-02).
      ...(mode === "edit" ? {} : { inmutable: false }),
    };
  };

  const onOk = async () => {
    // Guard anti doble-submit: si ya estamos enviando, ignoramos clicks
    // adicionales. Redundante con `confirmLoading` pero defensivo.
    if (submitting) return;
    if (!range) return;

    try {
      const values = await form.validateFields();

      const startTime: Dayjs | undefined = values.startTime;
      const endTime: Dayjs | undefined = values.endTime;

      if (!startTime || !endTime) return;

      const startHHMM = startTime.format("HH:mm");
      const endHHMM = endTime.format("HH:mm");
      const startThird: Third = minToThird(hhmmToMin(startHHMM));
      const endThird: Third = minToThird(hhmmToMin(endHHMM));

      // ============================================================
      // VALIDACIÓN TEMPORAL POR DATETIME COMPLETO
      // ============================================================
      // Bug anterior: comparábamos `range.startDate === range.endDate`
      // (fechas del CLICK original, no del form). Si el admin clickeaba
      // un día y cambiaba `salida` a día siguiente en el DatePicker, la
      // validación seguía creyendo que era mismo día y rechazaba
      // evening (15:00) → morning (10:59 al día siguiente) con un falso
      // "el tercio final debe ser posterior al inicial".
      //
      // Fix: construimos startDateTime y endDateTime combinando la
      // FECHA (del form para booking/block/maintenance, del range para
      // cleaning) con la HORA (del TimePicker). Comparamos como un
      // único instante en el tiempo. Así un rango evening→morning que
      // cruza medianoche es válido (end > start como datetime), y solo
      // se rechaza cuando end <= start como instante real.
      // ============================================================
      const asDateStr = (v: any, fallback: string): string => {
        if (!v) return fallback;
        if (typeof v === "string") return v;
        return v.format("YYYY-MM-DD");
      };
      // Los 4 kinds exponen DatePicker de ingreso/salida (cleaning se
      // sumó a la lista de date-driven en 2026-04-26 — antes su fecha
      // venía 100% del range original). Mantengo `isDateDrivenKind`
      // como `true` para no romper la nomenclatura del fieldName de
      // error más abajo.
      const isDateDrivenKind = true;
      const effectiveStartDate: string = asDateStr(
        values.ingreso,
        range.startDate,
      );
      const effectiveEndDate: string = asDateStr(
        values.salida,
        range.endDate,
      );

      const startDateTime = dayjs(`${effectiveStartDate}T${startHHMM}:00`);
      const endDateTime = dayjs(`${effectiveEndDate}T${endHHMM}:59`);

      if (!startDateTime.isValid() || !endDateTime.isValid()) {
        form.setFields([
          { name: "salida", errors: ["Fecha/hora inválida"] },
        ]);
        return;
      }

      if (!endDateTime.isAfter(startDateTime)) {
        // Reportamos el error sobre el DatePicker de salida — todos
        // los kinds (incluido cleaning desde 2026-04-26) lo exponen.
        const fieldName = isDateDrivenKind ? "salida" : "endTime";
        form.setFields([
          {
            name: fieldName,
            errors: [
              "La fecha/hora de fin debe ser posterior al inicio (se compara día + hora como un solo instante).",
            ],
          },
        ]);
        return;
      }

      // Mismo día (inicio y fin en la misma fecha): el tercio final
      // debe ser posterior o igual al inicial. Este check es redundante
      // con el datetime-check (end > start ya lo cubre en el mismo
      // día), pero da un mensaje más claro cuando el error viene de
      // cruzar tercios sin cambiar el día.
      const sameDay = effectiveStartDate === effectiveEndDate;
      if (sameDay && thirdIdx(startThird) > thirdIdx(endThird)) {
        form.setFields([
          {
            name: "endThird",
            errors: [
              "El tercio final debe ser posterior o igual al inicial dentro del mismo día",
            ],
          },
        ]);
        return;
      }

      // ============================================================
      // CHECK DE OVERLAP A NIVEL DE TERCIO (ADMIN/SUPERADMIN)
      // ============================================================
      // Regla nueva: admin tampoco puede crear ni editar un evento que
      // pise tercios ya usados por otros eventos de la misma propiedad.
      // En edit-mode excluimos el evento actual (para no chocar consigo
      // mismo). `checkOverlap` viene inyectado desde calendario.tsx.
      if (checkOverlap) {
        const excludeId =
          mode === "edit" ? (initialValues?.id as any) ?? null : null;
        const excludeKind =
          mode === "edit" ? (initialValues?.kind as any) ?? null : null;
        const conflict = checkOverlap({
          propertyId: range.propertyId,
          startDate: effectiveStartDate,
          endDate: effectiveEndDate,
          startThird,
          endThird,
          excludeId,
          excludeKind,
        });
        if (conflict) {
          form.setFields([{ name: "salida", errors: [conflict] }]);
          return;
        }
      }

      const payload = buildPayload(values);

      // Advertencia «esta aseadora ya tiene otro aseo ese día» (round
      // 2026-09-11). Solo aseos con aseadoras: pide confirmación si cambió el
      // equipo o las fechas y hay cruces — no bloquea. En edición, `original`
      // es lo que estaba guardado y el aseo editado no cuenta como cruce.
      // Ver Pages/aseos/components/AvisoAseosMismoDia.tsx.
      const iv = initialValues as any;
      const aseoEditadoId =
        mode === "edit" && iv?.kind === "cleaning" ? Number(iv?.id) : null;
      const consultaCruce =
        values.kind === "cleaning" && values.modoAsignacion !== "abierto"
          ? consultaMismoDia({
              usuarioIds:
                Array.isArray(values.asignaciones) && values.asignaciones.length > 0
                  ? values.asignaciones.map((a: any) => a?.usuario_id)
                  : [values.idAseador],
              desde: effectiveStartDate,
              hasta: effectiveEndDate,
              excluirAseoId: aseoEditadoId,
            })
          : null;
      const cruceOriginal =
        aseoEditadoId != null && iv?.modoAsignacion !== "abierto"
          ? consultaMismoDia({
              usuarioIds:
                Array.isArray(iv?.idAseadores) && iv.idAseadores.length > 0
                  ? iv.idAseadores
                  : [iv?.idAseador],
              desde: iv?.ingreso,
              hasta: iv?.salida,
              excluirAseoId: aseoEditadoId,
            })
          : null;

      // A partir de aquí disparamos el POST/PATCH. Bloqueamos el botón OK
      // hasta que termine (éxito o error) para evitar duplicación del evento
      // (también mientras se consulta y confirma el aviso de arriba).
      setSubmitting(true);
      try {
        if (!(await confirmarAseosMismoDia(consultaCruce, cruceOriginal))) return;
        if (mode === "edit") {
          await updateEvent(payload);
        } else {
          // `onCreate` puede ser sync o async — siempre `await` por si
          // retorna Promise (el caller la declara así en calendario.tsx).
          await onCreate(payload);
        }
      } finally {
        setSubmitting(false);
      }
    } catch (err: any) {
      if (Array.isArray(err?.errorFields) && err.errorFields.length) {
        const firstNamePath = err.errorFields[0].name;

        form.scrollToField(firstNamePath, {
          behavior: "smooth",
          block: "center",
        });

        const inst = (form as any).getFieldInstance?.(firstNamePath);
        inst?.focus?.();

        return;
      }

      // ✅ Error real
      getMsg()?.error(err?.message ?? "Error inesperado");
    }
  };

  const kinds = allowedKinds ?? ["cleaning", "booking", "block", "maintenance"];

  const labels = {
    cleaning: "Aseo",
    booking: "Arriendo",
    block: "Bloqueo",
    maintenance: "Mantención",
  };

  // Código de estadía AF a mostrar en el título en modo EDICIÓN: para un
  // arriendo es su propio id; para un aseo es la estadía vinculada
  // (reservaId, si viene del backend). Deja explícito sobre qué estadía se
  // está operando. Round 2026-07-16.
  const editStadiaId =
    mode === "edit"
      ? kind === "cleaning"
        ? ((initialValues as any)?.reservaId ?? null)
        : kind === "booking"
          ? ((initialValues?.id as any) ?? null)
          : null
      : null;

  return (
    <Modal
      title={
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {mode === "edit" ? `Editar ${kindTitle}` : `Crear ${kindTitle}`}
          {editStadiaId != null && <EstadiaCodeTag id={editStadiaId} />}
        </span>
      }
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText={mode === "edit" ? "Guardar" : "Crear"}
      cancelText="Cancelar"
      // Spinner + botón OK deshabilitado mientras el POST/PATCH está en curso.
      // Cancel también queda deshabilitado para evitar cerrar el modal a la
      // mitad y perder el state (el handler sigue corriendo en background).
      confirmLoading={submitting}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={!submitting}
      keyboard={!submitting}
      width={isMobile ? "95vw" : 1100}
      style={isMobile ? { maxWidth: "95vw" } : undefined}
      // Altura fija con scroll interno: permite que el Resumen del reparto
      // (3 tarjetas resaltadas al final) se quede sticky al fondo del modal
      // y siga visible mientras el admin edita precios, extras o descuentos.
      styles={{
        body: {
          maxHeight: "calc(90vh - 110px)",
          overflowY: "auto",
          paddingRight: 12,
        },
      }}
      destroyOnClose
    >
      {!canRender ? (
        <Alert type="info" message="Selecciona un rango válido" />
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space direction="vertical" size={0}>
                <Text strong>
                  {formatPropertyName(range!.propertyName) || "Propiedad"} —{" "}
                  {range!.startDate} → {range!.endDate}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  La <strong>hora</strong> se restringe al tercio elegido. Al
                  cambiar el tercio, la hora se reajusta al inicio/fin del
                  nuevo. Los días con los 3 tercios ocupados se bloquean en
                  los date-pickers; no puedes crear encima de un tercio
                  usado por otro evento.
                </Text>
              </Space>
            }
          />

          <Divider />

          <Form
            form={form}
            layout="vertical"
            scrollToFirstError={{ behavior: "smooth", block: "center" }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="kind"
                  label="Tipo"
                  initialValue={kinds[0]}
                  rules={[{ required: true, message: "Seleccione un tipo" }]}
                >
                  <Select disabled={mode === "edit"}>
                    {kinds.map((kind) => (
                      <Select.Option key={kind} value={kind}>
                        {labels[kind]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              {/*
               * Layout uniforme: para TODOS los tipos (cleaning/booking/
               * block/maintenance) la fila superior sólo contiene "Tipo".
               * Los tercios + horas (y, cuando aplica, las fechas de
               * entrada/salida) se agrupan dentro de cada kind-block más
               * abajo en secciones "Entrada" y "Salida" para que la
               * estructura espacial sea idéntica entre tipos (ver
               * docs/modulo-arriendos-precios.md §Layout del modal).
               */}
            </Row>

            {/* ====== CLEANING ====== */}
            {kind === "cleaning" && (
              <CleaningFields
                form={form}
                startThirdWatch={startThirdWatch}
                endThirdWatch={endThirdWatch}
                modoAsignacion={modoAsignacion}
                aseoCostPreset={aseoCostPreset}
                onAseoPresetChange={handleAseoPresetChange}
                aseoCostoTotal={aseoCostoTotal}
                esPasado={esPasado}
                esFuturo={esFuturo}
                loadingAseadoras={loadingAseadoras}
                aseadorOptions={aseadorOptions}
                ingreso={ingreso}
                range={range}
                isDayFullyOccupied={isDayFullyOccupied}
                // Un aseo en edición es VINCULADO si trae reserva_id (lo siembra
                // calendario.tsx en editInitialValues.reservaId). Deshabilita
                // "Propietario/Cliente paga" para no permitir doble cobro — el
                // backend igual lo rechaza, esto alinea la UX con /aseos.
                esVinculado={
                  mode === "edit" && (initialValues as any)?.reservaId != null
                }
                // Round 2026-07-06 — notas del aseo dentro del modal. En
                // edición pasamos el id del aseo para embeber el editor de las
                // 3 notas; en creación queda solo el texto "Nota para el
                // aseador".
                mode={mode}
                // Blindaje (defensa en profundidad) del fix del kind de arriba:
                // solo pasamos aseoId cuando el evento editado ES realmente un
                // aseo (`kind === "cleaning"`). Así, aunque el gate de `kind`
                // fallara y CleaningFields se montara editando un booking, JAMÁS
                // se envía un id de RESERVA a /aseos/:id/notas (evita el 404).
                aseoId={
                  mode === "edit" &&
                  (initialValues as any)?.kind === "cleaning"
                    ? ((initialValues?.id as any) ?? null)
                    : null
                }
              />
            )}

            {/* ====== BLOCK / MAINTENANCE ====== */}
            {(kind === "block" || kind === "maintenance") && (
              <BlockMaintenanceFields
                kind={kind}
                startThirdWatch={startThirdWatch}
                endThirdWatch={endThirdWatch}
                ingreso={ingreso}
                range={range}
                isDayFullyOccupied={isDayFullyOccupied}
                editExclude={
                  mode === "edit"
                    ? {
                        id: (initialValues?.id as any) ?? null,
                        kind: (initialValues?.kind as any) ?? null,
                      }
                    : null
                }
              />
            )}

            {/* ====== BOOKING ====== */}
            {kind === "booking" && (
              <>
                <Divider orientation="left">Arriendo</Divider>

                {/*
                 * Horario del arriendo: Fecha + Tercio + Hora de entrada y
                 * de salida se agrupan JUNTOS porque describen el horario
                 * como un conjunto coherente (ver
                 * docs/modulo-arriendos-precios.md §Layout del modal).
                 *
                 * Si la selección del calendario fue una sola fecha, el
                 * DatePicker de "Salida" queda vacío para que el admin la
                 * complete (ver useEffect `leaveSalidaEmpty`).
                 */}
                {/*
                 * Layout compacto: Entrada y Salida lado a lado en pantallas
                 * lg+ (mitad del ancho cada una, con 3 sub-campos
                 * fecha/tercio/hora). En md/xs caen a full-width pero
                 * mantienen las 3 columnas internas. Reduce scroll vertical
                 * a la mitad sin sacrificar legibilidad.
                 */}
                <Row gutter={24} style={{ marginTop: 8 }}>
                  <Col xs={24} lg={12}>
                    <Text strong style={{ display: "block", marginBottom: 6 }}>
                      Entrada
                    </Text>
                    <Row gutter={8}>
                      <Col xs={24} sm={10}>
                        <Form.Item
                          name="ingreso"
                          label="Fecha"
                          rules={[
                            { required: true, message: "Entrada es obligatoria" },
                          ]}
                        >
                          <DatePicker
                            format="DD-MM-YYYY"
                            style={{ width: "100%" }}
                            placeholder="Fecha"
                            disabledDate={(current) => {
                              if (!current) return false;
                              if (!range || !isDayFullyOccupied) return false;
                              // En edición excluimos el propio evento (y sus
                              // satélites) por id: sus tercios no cuentan como
                              // ocupados, pero cualquier OTRO evento sí bloquea.
                              return isDayFullyOccupied(
                                range.propertyId,
                                current.format("YYYY-MM-DD"),
                                mode === "edit"
                                  ? {
                                      id: (initialValues?.id as any) ?? null,
                                      kind: (initialValues?.kind as any) ?? null,
                                    }
                                  : undefined,
                              );
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={7}>{<StartThirdField />}</Col>
                      <Col xs={12} sm={7}>{<StartTimeField third={startThirdWatch} />}</Col>
                    </Row>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Text strong style={{ display: "block", marginBottom: 6 }}>
                      Salida
                    </Text>
                    <Row gutter={8}>
                      <Col xs={24} sm={10}>
                        <Form.Item
                          name="salida"
                          label="Fecha"
                          rules={[
                            { required: true, message: "Salida es obligatoria" },
                          ]}
                        >
                          <DatePicker
                            format="DD-MM-YYYY"
                            style={{ width: "100%" }}
                            placeholder="Fecha"
                            disabledDate={(current) => {
                              if (!current) return false;
                              const i = ingreso;
                              if (i) {
                                const iDay = dayjs.isDayjs(i) ? i : dayjs(i);
                                if (current.isBefore(iDay, "day")) return true;
                              }
                              if (range && isDayFullyOccupied) {
                                // Ídem Entrada: excluir el propio evento por id.
                                return isDayFullyOccupied(
                                  range.propertyId,
                                  current.format("YYYY-MM-DD"),
                                  mode === "edit"
                                    ? {
                                        id: (initialValues?.id as any) ?? null,
                                        kind: (initialValues?.kind as any) ?? null,
                                      }
                                    : undefined,
                                );
                              }
                              return false;
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={7}>{<EndThirdField />}</Col>
                      <Col xs={12} sm={7}>{<EndTimeField third={endThirdWatch} />}</Col>
                    </Row>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={12} md={6}>
                    <Form.Item name="noches" label="Cantidad de noches">
                      <InputNumber style={{ width: "100%" }} readOnly />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="nombreHuesped"
                      label="Nombre huésped"
                      rules={[
                        {
                          required: true,
                          message: "Nombre huésped es obligatorio",
                        },
                      ]}
                    >
                      <Input placeholder="Ej: Juan Pérez" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={4}>
                    <Form.Item
                      name="telefonoHuesped"
                      label="Teléfono (opcional)"
                    >
                      <Input placeholder="+56..." />
                    </Form.Item>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Item
                      name="cantidadHuespedes"
                      label="Cantidad de huéspedes"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        placeholder="0"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item name="fechaCreacion" label="Fecha de creación">
                      <DatePicker
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Precios</Divider>

                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="precioNocheClp"
                      label={
                        <span>
                          Precio de cada noche (CLP){" "}
                          <Tooltip title="Tarifa BASE por una noche (las no-extensión). Si no hay extensiones ni salida tardía, aplica a todas las noches. Si hay extensiones o salida tardía, el total se calcula ponderado: base × noches-base + Σ (noches-extensión × precio-extensión) + monto salida tardía (repartido por igual entre todas las noches). Bidireccional con 'Total por las noches'.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </span>
                      }
                      extra={
                        ((Array.isArray(extensionesNochesWatch) &&
                          extensionesNochesWatch.length > 0) ||
                          Number(salidaTardiaWatch?.monto_clp || 0) > 0) &&
                        Number(nochesWatch || 0) > 0 ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Promedio ponderado (extensiones + salida tardía):
                            {" $"}
                            {Math.round(
                              (Number(precioNochesClp) || 0) /
                                Math.max(1, Number(nochesWatch) || 1),
                            ).toLocaleString("es-CL")}
                            /noche
                          </Text>
                        ) : null
                      }
                      rules={[
                        {
                          required: true,
                          message: "Precio por noche es obligatorio",
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={clpInputFormatter} parser={clpInputParser}
                        placeholder="0"
                        onChange={() => {
                          lastEditedPriceRef.current = "noche";
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="precioNochesClp"
                      label={
                        <span>
                          Total por las noches (CLP){" "}
                          <Tooltip title="Total del arriendo por todas las noches. Si hay extensiones, es el TOTAL PONDERADO: base × noches-base + Σ (noches-extensión × precio-extensión). Editable bidireccional con 'Precio de cada noche' (al editar aquí se recalcula el precio base). Los descuentos tipo 'ajuste al total' se aplican sobre este valor.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={clpInputFormatter} parser={clpInputParser}
                        placeholder="0"
                        onChange={() => {
                          lastEditedPriceRef.current = "noches";
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      name="limpiezaClp"
                      label={
                        <span>
                          Total de la limpieza (CLP){" "}
                          <Tooltip title="Tarifa total de limpieza para este arriendo. Por defecto sale del total configurado en la propiedad (pago aseador + transporte total + materiales total + lavandería total). Puedes personalizarla usando el 'Desglose de limpieza' abajo. El Cliente la paga completa: se resta entera de su ingreso. Los subitems se redistribuyen: pago aseador al aseador, transporte/materiales (netos) a AF empresa, lavandería (neto) a lavandería AF si la propiedad tiene una asociada; los IVAs internos van al SII.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </span>
                      }
                      rules={[
                        {
                          // Acepta 0 como valor válido (preset "costo_0" o
                          // desglose personalizado con todos los subitems
                          // en 0). Sólo rechaza null/undefined.
                          validator: (_, value) =>
                            value === null || value === undefined
                              ? Promise.reject(
                                  new Error(
                                    "Selecciona un modo de limpieza (incluso 0 es válido)",
                                  ),
                                )
                              : Promise.resolve(),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={clpInputFormatter} parser={clpInputParser}
                        // §27: SIEMPRE read-only. La cifra se edita desde el
                        // Desglose de abajo (Personalizada), no acá con
                        // teclado. Eso evita discrepancias entre la suma de
                        // subitems y el total mostrado (bug del delta).
                        readOnly
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/*
                 * Desglose de limpieza — override per-arriendo (§23 + §27 docs).
                 * §27: el panel viene COLAPSADO. El admin lo expande solo si
                 * va a usar algo distinto a los valores de la propiedad. El
                 * input "Limpieza (CLP)" de arriba es siempre read-only y
                 * refleja la suma de los subitems efectivos.
                 *
                 * `forceRender: true` mantiene los Form.Item montados incluso
                 * cuando el panel está cerrado — sino useWatch perdería el
                 * tracking de limpiezaPreset / limpiezaCustom* y los
                 * effects de sincronización no correrían.
                 */}
                <Collapse
                  bordered
                  style={{ marginBottom: 16, background: "#FAFAFA" }}
                  items={[
                    {
                      key: "desglose-limpieza",
                      forceRender: true,
                      label: (
                        <Space>
                          <Text strong>Desglose de limpieza</Text>
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, fontWeight: 400 }}
                          >
                            — {limpiezaPresetLabel}
                          </Text>
                          <Tooltip title="Permite usar un cobro de limpieza distinto al de la propiedad SOLO para este arriendo. No altera la propiedad — se guarda como override por reserva. Útil para reservas largas, promociones, o cuando no corresponde la lavandería.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      ),
                      children: (
                        <>
                          <Form.Item
                            name="limpiezaPreset"
                            label="Modo"
                            tooltip="Elige cómo se compone la limpieza de este arriendo. 'Por defecto' usa los subitems configurados en la propiedad."
                            initialValue="default"
                            style={{ marginBottom: 12 }}
                          >
                            <Select
                              style={{ maxWidth: 320 }}
                              // Fix 2026-08-13 — el repintado de los 4 inputs
                              // vive acá (evento del admin), no en un efecto.
                              onChange={handleLimpiezaPresetChange}
                            >
                              <Select.Option value="default">
                                Por defecto (propiedad)
                              </Select.Option>
                              <Select.Option value="sin_lavanderia">
                                Sin lavandería (lavandería = $0)
                              </Select.Option>
                              <Select.Option value="costo_0">
                                Costo 0 (limpieza gratis)
                              </Select.Option>
                              <Select.Option value="custom">
                                Personalizada
                              </Select.Option>
                            </Select>
                          </Form.Item>

                          {/* Tabla de subitems efectivos. En "custom" son inputs
                              editables; en los otros presets son read-only
                              reflejando lo que se va a aplicar. §27 */}
                          <Row gutter={12}>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item
                                name="limpiezaCustomAseador"
                                label="Pago aseador"
                                tooltip="Sin IVA (persona natural)."
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                  readOnly={limpiezaPreset !== "custom"}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item
                                name="limpiezaCustomTransporteTotal"
                                label="Transporte (total)"
                                tooltip="Con IVA incluido. Neto = total/1.19 se acredita a Aloja Fácil Empresa."
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                  readOnly={limpiezaPreset !== "custom"}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item
                                name="limpiezaCustomMaterialesTotal"
                                label="Materiales (total)"
                                tooltip="Con IVA incluido. Neto = total/1.19 se acredita a Aloja Fácil Empresa."
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                  readOnly={limpiezaPreset !== "custom"}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item
                                name="limpiezaCustomLavanderiaTotal"
                                label="Lavandería (total)"
                                tooltip={
                                  <span>
                                    Con IVA incluido. Si la propiedad usa
                                    una <b>lavandería externa</b>, el monto
                                    suma a la utilidad AF Empresa. Si usa
                                    una <b>lavandería propia AF</b>, el
                                    dinero va a las finanzas internas de esa
                                    lavandería y NO entra a AF Empresa
                                    (rol intermediario). La asignación
                                    propiedad → lavandería se configura en
                                    `/propiedades`.
                                  </span>
                                }
                                style={{ marginBottom: 8 }}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                  readOnly={limpiezaPreset !== "custom"}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Suma de subitems ={" "}
                            <strong>
                              $
                              {Math.round(limpiezaFromSubitems).toLocaleString(
                                "es-CL",
                              )}
                            </strong>{" "}
                            — este valor alimenta "Limpieza (CLP)" de arriba.
                          </Text>
                        </>
                      ),
                    },
                  ]}
                />

                {/*
                 * §28 — Cargos adicionales del arriendo. 3 sub-bloques:
                 *   - Extensión de noches: noches extra a precio especial.
                 *   - Salida tardía: cobro + nueva hora.
                 *   - Otros: flujos ad-hoc con rolAF + pagado_por/a.
                 * Cada uno en su propio Collapse panel (arrancan todos
                 * cerrados — el admin los expande sólo si los va a usar).
                 */}
                <Collapse
                  bordered
                  style={{ marginBottom: 16, background: "#FAFAFA" }}
                  items={[
                    {
                      key: "extensiones-noches",
                      forceRender: true,
                      label: (
                        <Space>
                          <Text strong>Extensión de noches</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {Array.isArray(extensionesNochesWatch) &&
                            extensionesNochesWatch.length > 0
                              ? `— ${extensionesNochesWatch.length} extensión(es) · +$${Math.round(totalExtensionesNoches).toLocaleString("es-CL")}`
                              : "— sin extensiones"}
                          </Text>
                          <Tooltip title="Agregá noches extra a un precio especial (negociado). Cada extensión solo aporta precio por noche × cantidad — NO dispara aseo, lavandería ni materiales. Comisiona Aloja Fácil Empresa al mismo % de la propiedad. Ejemplo: 2 noches extra a $40.000 c/u.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      ),
                      children: (
                        <Form.List name="extensionesNoches">
                          {(fields, { add, remove }) => (
                            <>
                              {fields.map(({ key, name, ...restField }) => (
                                <Space
                                  key={key}
                                  style={{
                                    display: "flex",
                                    marginBottom: 8,
                                    flexWrap: "wrap",
                                  }}
                                  align="baseline"
                                >
                                  <Form.Item
                                    {...restField}
                                    name={[name, "noches"]}
                                    label="Noches extra"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Noches",
                                      },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={1}
                                      step={1}
                                      style={{ width: 120 }}
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "precio_por_noche_clp"]}
                                    label="Precio de cada noche"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Precio",
                                      },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={0}
                                      style={{ width: 160 }}
                                      formatter={clpInputFormatter} parser={clpInputParser}
                                      placeholder="0"
                                    />
                                  </Form.Item>
                                  <a onClick={() => remove(name)}>Eliminar</a>
                                </Space>
                              ))}
                              <a
                                onClick={() =>
                                  add({
                                    noches: 1,
                                    precio_por_noche_clp: null,
                                  })
                                }
                              >
                                + Agregar extensión
                              </a>
                            </>
                          )}
                        </Form.List>
                      ),
                    },
                    {
                      key: "salida-tardia",
                      forceRender: true,
                      label: (
                        <Space>
                          <Text strong>Salida tardía</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {totalSalidaTardia > 0
                              ? `— +$${Math.round(totalSalidaTardia).toLocaleString("es-CL")}${salidaTardiaWatch?.hora_nueva ? ` · nueva hora ${salidaTardiaWatch.hora_nueva}` : ""}`
                              : "— sin salida tardía"}
                          </Text>
                          <Tooltip title="Cobro adicional al huésped por quedarse después de la hora estándar de salida (11:00). Modifica SOLO la hora de salida — no toca fecha, ni dispara aseo extra, ni lavandería. Comisiona Aloja Fácil Empresa al % de la propiedad.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      ),
                      children: (
                        <Row gutter={16}>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              name={["salidaTardia", "monto_clp"]}
                              label="Monto cobrado"
                              tooltip="Cargo extra al huésped por la salida tardía. Ejemplo: $15.000."
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                formatter={clpInputFormatter} parser={clpInputParser}
                                placeholder="0"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8}>
                            <Form.Item
                              name={["salidaTardia", "hora_nueva"]}
                              label="Nueva hora de salida"
                              tooltip="Hora límite de salida negociada con el huésped. Ejemplo: '14:00' (si la normal es 11:00)."
                              style={{ marginBottom: 8 }}
                            >
                              <Input placeholder="14:00" />
                            </Form.Item>
                          </Col>
                        </Row>
                      ),
                    },
                    {
                      key: "otros-cargos",
                      forceRender: true,
                      label: (
                        <Space>
                          <Text strong>Otros cargos</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {Array.isArray(otrosCargosWatch) &&
                            otrosCargosWatch.length > 0
                              ? `— ${otrosCargosWatch.length} cargo(s)`
                              : "— sin otros cargos"}
                          </Text>
                          <Tooltip title="Flujos de dinero ad-hoc asociados a este arriendo: cobros por daños, reembolsos, servicios adicionales (tours, chofer, etc). Cada uno especifica rol de Aloja Fácil Empresa (paga/recibe/intermediario) + pagado_por + pagado_a. NO entra directamente en 'Reserva total' — se registra en los ledgers de cada entidad y aparece en los reportes financieros.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      ),
                      children: (
                        <Form.List name="otrosCargos">
                          {(fields, { add, remove }) => (
                            <>
                              {fields.map(({ key, name, ...restField }) => (
                                <Card
                                  key={key}
                                  size="small"
                                  style={{ marginBottom: 8 }}
                                  extra={
                                    <a onClick={() => remove(name)}>Eliminar</a>
                                  }
                                >
                                  <Row gutter={12}>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "nombre"]}
                                        label="Descripción"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Descripción",
                                          },
                                        ]}
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Input placeholder="Ej: Cobro por daño a TV" />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "monto_clp"]}
                                        label="Monto"
                                        rules={[
                                          {
                                            required: true,
                                            message: "Monto",
                                          },
                                        ]}
                                        style={{ marginBottom: 8 }}
                                      >
                                        <InputNumber
                                          style={{ width: "100%" }}
                                          min={0}
                                          formatter={clpInputFormatter} parser={clpInputParser}
                                          placeholder="0"
                                        />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={6}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "rol_af"]}
                                        label="Rol Aloja Fácil Empresa"
                                        tooltip="paga: Aloja Fácil Empresa desembolsa. recibe: Aloja Fácil Empresa cobra. intermediario: Aloja Fácil Empresa pasa el dinero entre dos terceros."
                                        rules={[
                                          {
                                            required: true,
                                            message: "Rol",
                                          },
                                        ]}
                                        initialValue="paga"
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Select>
                                          <Select.Option value="paga">
                                            Paga
                                          </Select.Option>
                                          <Select.Option value="recibe">
                                            Recibe
                                          </Select.Option>
                                          <Select.Option value="intermediario">
                                            Intermediario
                                          </Select.Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  <Row gutter={12}>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "pagado_por", "tipo"]}
                                        label="Pagado por"
                                        tooltip="Quién desembolsa el dinero. 0=Aloja Fácil Empresa, 1=Cliente (propietario), 2=Arrendatario (huésped), 3=Aseador, 4=Lavandería, 5=Otro."
                                        rules={[
                                          {
                                            required: true,
                                            message: "Seleccioná",
                                          },
                                        ]}
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Select>
                                          <Select.Option value={0}>
                                            Aloja Fácil Empresa
                                          </Select.Option>
                                          <Select.Option value={1}>
                                            Cliente (propietario)
                                          </Select.Option>
                                          <Select.Option value={2}>
                                            Arrendatario (huésped)
                                          </Select.Option>
                                          <Select.Option value={3}>
                                            Aseador
                                          </Select.Option>
                                          <Select.Option value={4}>
                                            Lavandería
                                          </Select.Option>
                                          <Select.Option value={5}>
                                            Otro
                                          </Select.Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "pagado_a", "tipo"]}
                                        label="Pagado a"
                                        tooltip="Quién recibe el dinero. Mismos códigos que 'Pagado por'."
                                        rules={[
                                          {
                                            required: true,
                                            message: "Seleccioná",
                                          },
                                        ]}
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Select>
                                          <Select.Option value={0}>
                                            Aloja Fácil Empresa
                                          </Select.Option>
                                          <Select.Option value={1}>
                                            Cliente (propietario)
                                          </Select.Option>
                                          <Select.Option value={2}>
                                            Arrendatario (huésped)
                                          </Select.Option>
                                          <Select.Option value={3}>
                                            Aseador
                                          </Select.Option>
                                          <Select.Option value={4}>
                                            Lavandería
                                          </Select.Option>
                                          <Select.Option value={5}>
                                            Otro
                                          </Select.Option>
                                        </Select>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              ))}
                              <a
                                onClick={() =>
                                  add({
                                    nombre: "",
                                    monto_clp: null,
                                    rol_af: "paga",
                                    pagado_por: { tipo: 0 },
                                    pagado_a: { tipo: 2 },
                                  })
                                }
                              >
                                + Agregar cargo ad-hoc
                              </a>
                            </>
                          )}
                        </Form.List>
                      ),
                    },
                  ]}
                />

                <Divider orientation="left">Comisiones y extras</Divider>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="comisionPlataformaClp"
                      label={
                        <span>
                          Comisión de la plataforma (opcional){" "}
                          <Tooltip title="Lo que cobra Airbnb / Booking / Despegar por gestionar este arriendo. Este valor YA INCLUYE IVA — no se le suma 19% adicional. Si la reserva es directa (sin plataforma), dejar en blanco.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </span>
                      }
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={clpInputFormatter} parser={clpInputParser}
                        placeholder="0"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card
                      size="small"
                      style={{
                        background: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        marginBottom: 12,
                      }}
                      styles={{ body: { padding: 12 } }}
                      title={
                        <Space size={6}>
                          <Text strong style={{ fontSize: 13 }}>
                            Extras sin comisión de Aloja Fácil
                          </Text>
                          <Tooltip title="Extras que paga el huésped y van DIRECTO al Cliente — Aloja Fácil no cobra comisión sobre ellos. Ejemplos: uso de parrilla, espacios comunes, multas del condominio, cargos del edificio. Se suman a la Reserva total.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Form.List name="extrasSinComisionClp">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.length === 0 && (
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  display: "block",
                                  marginBottom: 8,
                                }}
                              >
                                Sin extras. Agregá uno con el botón de abajo.
                              </Text>
                            )}
                            {fields.map(({ key, name, ...restField }) => (
                              <Row
                                key={key}
                                gutter={8}
                                align="top"
                                style={{ marginBottom: 8 }}
                              >
                                <Col flex="auto">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "name"]}
                                    rules={[
                                      { required: true, message: "Nombre" },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input placeholder="Concepto (ej. parrilla)" />
                                  </Form.Item>
                                </Col>
                                <Col flex="0 0 150px">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "_f_val"]}
                                    rules={[
                                      { required: true, message: "Monto" },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      style={{ width: "100%" }}
                                      min={0}
                                      formatter={clpInputFormatter} parser={clpInputParser}
                                      placeholder="0"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col flex="0 0 40px">
                                  <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                    aria-label="Eliminar extra"
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              block
                              icon={<PlusOutlined />}
                              onClick={() =>
                                add({ name: "", _f_val: null })
                              }
                            >
                              Agregar extra
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  </Col>

                  <Col xs={24} md={12}>
                    <Card
                      size="small"
                      style={{
                        background: "#f9f0ff",
                        border: "1px solid #d3adf7",
                        marginBottom: 12,
                      }}
                      styles={{ body: { padding: 12 } }}
                      title={
                        <Space size={6}>
                          <Text strong style={{ fontSize: 13 }}>
                            Extras con comisión de Aloja Fácil
                          </Text>
                          <Tooltip title="Extras que paga el huésped y SÍ pagan comisión a Aloja Fácil (misma tasa que las noches). Ejemplos: tarifas por mascotas, personas adicionales, check-in temprano. Se suman a la Reserva total.">
                            <InfoCircleOutlined style={{ opacity: 0.7 }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Form.List name="extrasConComisionClp">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.length === 0 && (
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  display: "block",
                                  marginBottom: 8,
                                }}
                              >
                                Sin extras. Agregá uno con el botón de abajo.
                              </Text>
                            )}
                            {fields.map(({ key, name, ...restField }) => (
                              <Row
                                key={key}
                                gutter={8}
                                align="top"
                                style={{ marginBottom: 8 }}
                              >
                                <Col flex="auto">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "name"]}
                                    rules={[
                                      { required: true, message: "Nombre" },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input placeholder="Concepto (ej. mascota)" />
                                  </Form.Item>
                                </Col>
                                <Col flex="0 0 150px">
                                  <Form.Item
                                    {...restField}
                                    name={[name, "_f_val"]}
                                    rules={[
                                      { required: true, message: "Monto" },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      style={{ width: "100%" }}
                                      min={0}
                                      formatter={clpInputFormatter} parser={clpInputParser}
                                      placeholder="0"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col flex="0 0 40px">
                                  <Button
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                    aria-label="Eliminar extra"
                                  />
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              block
                              icon={<PlusOutlined />}
                              onClick={() =>
                                add({ name: "", _f_val: null })
                              }
                            >
                              Agregar extra
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  </Col>
                </Row>

                <Row gutter={16} style={{ marginTop: 4 }}>
                  <Col xs={24} md={{ span: 12, offset: 6 }}>
                    <Form.Item
                      name="totalExtrasClp"
                      label="Total de los extras (automático)"
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        readOnly
                        formatter={clpInputFormatter} parser={clpInputParser}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Descuentos{" "}
                  <Tooltip
                    title={
                      <span>
                        Dos tipos de descuento:
                        <br />
                        <strong>Ajuste al total:</strong> el huésped pagó menos. Reduce el total efectivo de noches y las comisiones de Aloja Fácil se recalculan en cascada. Cliente y Aloja Fácil pierden proporcional.
                        <br />
                        <strong>Solo Aloja Fácil:</strong> pérdida exclusiva de Aloja Fácil. El Cliente conserva su ingreso original.
                      </span>
                    }
                  >
                    <InfoCircleOutlined style={{ opacity: 0.7 }} />
                  </Tooltip>
                </Text>
                <Form.List name="descuentoClp">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space
                          key={key}
                          style={{ display: "flex", marginBottom: 8 }}
                          align="baseline"
                        >
                          <Form.Item
                            {...restField}
                            name={[name, "name"]}
                            rules={[{ required: true, message: "Nombre" }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="Nombre" />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "_f_val"]}
                            rules={[{ required: true, message: "Monto" }]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              style={{ width: 140 }}
                              min={0}
                              formatter={clpInputFormatter} parser={clpInputParser}
                              placeholder="0"
                            />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "tipo"]}
                            rules={[{ required: true, message: "Tipo" }]}
                            style={{ marginBottom: 0 }}
                            initialValue="ajuste_total"
                          >
                            <Select style={{ width: 180 }}>
                              <Select.Option value="ajuste_total">
                                Ajuste al total
                              </Select.Option>
                              <Select.Option value="solo_af">
                                Solo Aloja Fácil
                              </Select.Option>
                            </Select>
                          </Form.Item>

                          <a onClick={() => remove(name)}>Eliminar</a>
                        </Space>
                      ))}

                      <a
                        onClick={() =>
                          add({ name: "", _f_val: null, tipo: "ajuste_total" })
                        }
                      >
                        + Agregar
                      </a>
                    </>
                  )}
                </Form.List>

                {/*
                 * Detalle de comisiones — campos read-only auto-calculados.
                 * Colapsado por defecto: el admin rara vez necesita verlos
                 * mientras crea una reserva; el Resumen de abajo ya muestra
                 * el Ingreso AF (que integra estos valores). forceRender en
                 * el Panel mantiene los Form.Items montados para que los
                 * useEffect de autocálculo sigan escribiendo aunque esté
                 * cerrado.
                 */}
                <Collapse
                  ghost
                  style={{ marginTop: 8, marginBottom: 8 }}
                  items={[
                    {
                      key: "af-auto",
                      forceRender: true,
                      label: (
                        <Text strong>
                          Detalle de las comisiones de Aloja Fácil (automático){" "}
                          <Text type="secondary" style={{ fontWeight: "normal" }}>
                            — click para expandir
                          </Text>
                        </Text>
                      ),
                      children: (
                        <>
                          <Row gutter={16}>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="tasaComisionAf"
                                label={
                                  <span>
                                    Tasa de comisión Aloja Fácil{" "}
                                    <Tooltip title="Porcentaje de comisión que Aloja Fácil cobra por administrar esta propiedad. Se define en el contrato de la propiedad. Ej: 0.10 = 10%.">
                                      <InfoCircleOutlined style={{ opacity: 0.7 }} />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  readOnly
                                  min={0}
                                  max={1}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                              <Form.Item
                                name="comisionAfNochesClp"
                                label={
                                  <span>
                                    Comisión Aloja Fácil sobre las noches{" "}
                                    <Tooltip title="Comisión que cobra Aloja Fácil sobre las noches NETAS de comisión de plataforma. Fórmula: (total noches efectivo − comisión de la plataforma) × tasa de comisión Aloja Fácil — la plata que retiene Airbnb/Booking nunca llega, no devenga comisión. En un arriendo directo la comisión de plataforma es 0 y la base es el total de las noches. Si hay descuento tipo 'ajuste al total', la base baja. No incluye IVA (ver campo siguiente).">
                                      <InfoCircleOutlined style={{ opacity: 0.7 }} />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  readOnly
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                              <Form.Item
                                name="ivaComisionDeNochesClp"
                                label={
                                  <span>
                                    IVA sobre la comisión de noches ({getIvaPorcentaje()}%){" "}
                                    <Tooltip
                                      title={`IVA que Aloja Fácil factura al Cliente sobre la comisión de noches. ${getIvaPorcentaje()}% × Comisión Aloja Fácil sobre las noches. La tasa se configura en /configuracion → Precios e IVA.`}
                                    >
                                      <InfoCircleOutlined style={{ opacity: 0.7 }} />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  readOnly
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Row gutter={16}>
                            <Col xs={24} md={8}>
                              <Form.Item
                                name="comisionAfExtrasClp"
                                label={
                                  <span>
                                    Comisión Aloja Fácil sobre los extras{" "}
                                    <Tooltip title="Comisión que cobra Aloja Fácil sobre los extras con comisión (mascotas, personas extra, salida tardía, etc.). Fórmula: suma de extras con comisión × tasa de comisión Aloja Fácil. Los extras sin comisión NO entran acá.">
                                      <InfoCircleOutlined style={{ opacity: 0.7 }} />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  readOnly
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                              <Form.Item
                                name="ivaComisionDeExtrasClp"
                                label={
                                  <span>
                                    IVA sobre la comisión de extras ({getIvaPorcentaje()}%){" "}
                                    <Tooltip
                                      title={`${getIvaPorcentaje()}% × Comisión Aloja Fácil sobre los extras. IVA que Aloja Fácil factura al Cliente. La tasa se configura en /configuracion → Precios e IVA.`}
                                    >
                                      <InfoCircleOutlined style={{ opacity: 0.7 }} />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  readOnly
                                  formatter={clpInputFormatter} parser={clpInputParser}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      ),
                    },
                  ]}
                />

                <Divider orientation="left">Resumen del reparto</Divider>

                {/*
                 * Form.Items ocultos: mantienen el binding de los 3 valores
                 * al form (el autocálculo los escribe vía setFieldsValue y
                 * el submit los lee). La UI visible son las tarjetas
                 * resaltadas de abajo — más claro que 3 InputNumber readOnly.
                 */}
                <Form.Item name="reservaTotalClp" hidden>
                  <InputNumber />
                </Form.Item>
                <Form.Item name="reservaTotalPropietario" hidden>
                  <InputNumber />
                </Form.Item>
                <Form.Item name="ingresoAfClp" hidden>
                  <InputNumber />
                </Form.Item>

                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 20,
                    background:
                      "linear-gradient(to top, rgba(255,255,255,1) 85%, rgba(255,255,255,0.85))",
                    paddingTop: 12,
                    paddingBottom: 8,
                    borderTop: "1px solid #f0f0f0",
                    marginTop: 8,
                  }}
                >
                  <Row gutter={12}>
                    <Col xs={24} md={8}>
                      <Card
                        size="small"
                        style={{
                          background: "#e6f4ff",
                          border: "1px solid #91caff",
                        }}
                        styles={{ body: { padding: "12px 14px" } }}
                      >
                        <Space direction="vertical" size={2} style={{ width: "100%" }}>
                          <Space size={4} style={{ color: "#0958d9" }}>
                            <Text strong style={{ color: "#0958d9", fontSize: 12 }}>
                              TOTAL QUE PAGA EL HUÉSPED
                            </Text>
                            <Tooltip title="Todo lo que paga el huésped por la reserva. Fórmula: total de noches (bruto, incluida la parte que retiene la plataforma) + total de la limpieza + extras (con y sin comisión). Los descuentos tipo 'ajuste al total' ya están aplicados sobre el total de noches.">
                              <InfoCircleOutlined style={{ color: "#0958d9", opacity: 0.7 }} />
                            </Tooltip>
                          </Space>
                          <Text strong style={{ fontSize: 20, color: "#002c8c" }}>
                            ${Math.round(n(reservaTotalClpWatch)).toLocaleString("es-CL")}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Reserva total
                          </Text>
                        </Space>
                      </Card>
                    </Col>

                    <Col xs={24} md={8}>
                      <Card
                        size="small"
                        style={{
                          background: "#f6ffed",
                          border: "1px solid #b7eb8f",
                        }}
                        styles={{ body: { padding: "12px 14px" } }}
                      >
                        <Space direction="vertical" size={2} style={{ width: "100%" }}>
                          <Space size={4} style={{ color: "#389e0d" }}>
                            <Text strong style={{ color: "#389e0d", fontSize: 12 }}>
                              GANANCIA DEL CLIENTE
                            </Text>
                            <Tooltip
                              title={
                                <span>
                                  Lo que recibe el Cliente (dueño/gestor de la propiedad).
                                  <br />
                                  <br />
                                  Fórmula: Total que paga el huésped
                                  <br />− Comisión de la plataforma
                                  <br />− Total de la limpieza
                                  <br />− Comisión Aloja Fácil sobre noches − IVA
                                  <br />− Comisión Aloja Fácil sobre extras − IVA
                                </span>
                              }
                            >
                              <InfoCircleOutlined style={{ color: "#389e0d", opacity: 0.7 }} />
                            </Tooltip>
                          </Space>
                          <Text strong style={{ fontSize: 20, color: "#135200" }}>
                            ${Math.round(n(reservaTotalPropietarioWatch)).toLocaleString("es-CL")}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Para el dueño/gestor
                          </Text>
                        </Space>
                      </Card>
                    </Col>

                    <Col xs={24} md={8}>
                      <Card
                        size="small"
                        style={{
                          background: "#f9f0ff",
                          border: "1px solid #d3adf7",
                        }}
                        styles={{ body: { padding: "12px 14px" } }}
                      >
                        <Space direction="vertical" size={2} style={{ width: "100%" }}>
                          <Space size={4} style={{ color: "#531dab" }}>
                            <Text strong style={{ color: "#531dab", fontSize: 12 }}>
                              GANANCIA DE ALOJA FÁCIL
                            </Text>
                            <Tooltip
                              title={
                                <span>
                                  Lo que gana Aloja Fácil (empresa) por este arriendo.
                                  <br />
                                  <br />
                                  Fórmula (ver doc flujo-financiero.md §4):
                                  <br />Comisión Aloja Fácil sobre noches
                                  <br />+ IVA de esa comisión
                                  <br />+ Comisión Aloja Fácil sobre extras con comisión
                                  <br />+ IVA de esa comisión
                                  <br />+ Otros cargos con rol "AF recibe" − Otros cargos con rol "AF paga"
                                  <br />− Descuentos tipo "solo Aloja Fácil"
                                  <br />
                                  <br />
                                  NO incluye ningún subítem de la limpieza (pago aseador, transporte, materiales, lavandería): esos montos ya están dentro de "Limpieza" y se contabilizan aparte (transporte + materiales entran a Aloja Fácil por la pestaña Aseos; la lavandería por su propia contabilidad).
                                </span>
                              }
                            >
                              <InfoCircleOutlined style={{ color: "#531dab", opacity: 0.7 }} />
                            </Tooltip>
                          </Space>
                          <Text strong style={{ fontSize: 20, color: "#22075e" }}>
                            ${Math.round(n(ingresoAfClpWatch)).toLocaleString("es-CL")}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Para la empresa
                          </Text>
                        </Space>
                      </Card>
                    </Col>
                  </Row>
                </div>

                {/*
                 * Diagrama visual del reparto del dinero. Muestra cómo la
                 * Reserva total se divide entre los 4 buckets (Plataforma,
                 * Limpieza, AF, Cliente) e incluye la redistribución de los
                 * subitems de limpieza. Ver docs/modulo-arriendos-precios.md
                 * §Flujo de dinero.
                 */}
                <Suspense fallback={null}>
                <ReparticionDineroDiagram
                  reservaTotal={n(reservaTotalClpWatch)}
                  comisionPlataforma={n(comisionPlataformaWatch)}
                  limpiezaTotal={n(limpiezaClpWatch)}
                  // Subitems efectivos (respetan el override per-arriendo,
                  // ej: "Sin lavandería" → lavanderiaTotal=0). Si el admin
                  // no tocó el desglose son los de la propiedad.
                  pagoAseador={effectiveLimpiezaSubitems.aseador}
                  transporteTotal={effectiveLimpiezaSubitems.transporteTotal}
                  materialesTotal={effectiveLimpiezaSubitems.materialesTotal}
                  lavanderiaTotal={effectiveLimpiezaSubitems.lavanderiaTotal}
                  hasLavanderiaAf={
                    Boolean(range?.hasLavanderiaAf) &&
                    effectiveLimpiezaSubitems.lavanderiaTotal > 0
                  }
                  ingresoAf={n(ingresoAfClpWatch)}
                  ingresoCliente={n(reservaTotalPropietarioWatch)}
                  // Descuentos + otros cargos: ajustan la invariante de
                  // conservación (doc flujo-financiero.md §12). Ver el
                  // comentario de `sumBuckets` en ReparticionDineroDiagram.
                  otrosCargosNetoAf={(
                    Array.isArray(otrosCargosWatch) ? otrosCargosWatch : []
                  ).reduce((acc: number, c: any) => {
                    const monto = Number(c?.monto_clp) || 0;
                    const rol = c?.rol_af;
                    if (rol === "recibe") return acc + monto;
                    if (rol === "paga") return acc - monto;
                    return acc;
                  }, 0)}
                  descSoloAf={(Array.isArray(descuentoClp) ? descuentoClp : [])
                    .filter((d: any) => d?.tipo === "solo_af")
                    .reduce(
                      (acc: number, d: any) => acc + (Number(d?._f_val) || 0),
                      0,
                    )}
                  descAjuste={(Array.isArray(descuentoClp) ? descuentoClp : [])
                    .filter(
                      (d: any) =>
                        (d?.tipo ?? "ajuste_total") === "ajuste_total",
                    )
                    .reduce(
                      (acc: number, d: any) => acc + (Number(d?._f_val) || 0),
                      0,
                    )}
                />
                </Suspense>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="estadoPago" label="Estado pago (opcional)">
                      <Select allowClear placeholder="Opcional">
                        <Select.Option value="pendiente">
                          Pendiente
                        </Select.Option>
                        <Select.Option value="pagado">Pagado</Select.Option>
                        <Select.Option value="parcial">Parcial</Select.Option>
                        <Select.Option value="reembolsado">
                          Reembolsado
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item name="tipoPago" label="Tipo pago (opcional)">
                      <Select allowClear placeholder="Opcional">
                        <Select.Option value="transferencia">
                          Transferencia
                        </Select.Option>
                        <Select.Option value="efectivo">Efectivo</Select.Option>
                        <Select.Option value="tarjeta">Tarjeta</Select.Option>
                        <Select.Option value="otro">Otro</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="notaInterna"
                      label="Nota interna (opcional)"
                    >
                      <Input.TextArea rows={3} placeholder="Texto interno..." />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </>
      )}
      {mode === "edit" && initialValues && (
        <Button
          danger
          // Cierra el modal por su cuenta (deleteEvent + onClose), por fuera del
          // onCancel que `confirmLoading` bloquea. Mientras guarda —quizás
          // esperando el aviso de aseos el mismo día, round 2026-09-11— queda
          // deshabilitado: si no, la confirmación aparecería sobre un modal
          // cerrado y «Guardar igual» intentaría guardar lo que se eliminó.
          disabled={submitting}
          onClick={() => {
            // Round 2026-05-01 — antes usaba `Modal.confirm` directo de
            // antd, que en v5 + React 19 NO renderiza el modal (root React
            // huérfano). Resultado: el botón "Eliminar" del calendario
            // editor no hacía absolutamente nada al hacer clic. Migrado
            // al bridge `getModal()`.
            const modal = getModal();
            const doDelete = async () => {
              // deleteEvent (calendario.tsx) ya refresca el calendario en su
              // lugar (refetchKeepScroll) — sin refetch extra acá, que
              // duplicaba el fetch y hacía parpadear el velo de refreshing.
              await deleteEvent(
                String(initialValues.kind),
                initialValues.id ?? "",
              );
              onClose();
            };
            if (!modal) {
              void doDelete();
              return;
            }
            modal.confirm({
              title: "¿Eliminar evento?",
              content: "Esta acción no se puede deshacer.",
              okText: "Eliminar",
              okButtonProps: { danger: true },
              cancelText: "Cancelar",
              onOk: doDelete,
            });
          }}
        >
          Eliminar
        </Button>
      )}
    </Modal>
  );
}
